import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import axios from 'axios';
import { 
  FileText, 
  Download, 
  RefreshCw,
  ChevronRight,
  Mail
} from 'lucide-react';
import { Toaster } from './components/ui/sonner';
import { toast } from 'sonner';

import { CustomerForm } from './components/CustomerForm';
import { AudiobookSection } from './components/AudiobookSection';
import { PodcastSection, PODCAST_ADDONS } from './components/PodcastSection';
import { WalkingPodcastSection } from './components/WalkingPodcastSection';
import { MeditacionesSection } from './components/MeditacionesSection';
import { EditorialSection } from './components/EditorialSection';
import { AudioCursosSection } from './components/AudioCursosSection';
import { ServiciosAdicionalesSection } from './components/ServiciosAdicionalesSection';
import { PaymentInfo } from './components/PaymentInfo';
import { TermsCheckbox } from './components/TermsCheckbox';
import AIChatWidget from './components/AIChatWidget';
import { AddonsSelector } from './components/AddonsSelector';
import { QuotePreview } from './components/QuotePreview';
import { SendEmailModal } from './components/SendEmailModal';
import { PackageDetailsModal } from './components/PackageDetailsModal';
import { RentaEquipoSection, calcRentaEquipoTotal } from './components/RentaEquipoSection';
import { TransmisionEnVivoSection, calcTransmisionTotal } from './components/TransmisionEnVivoSection';
import { SonidoDirectoSection, calcSonidoDirectoTotal } from './components/SonidoDirectoSection';
import { ForoSection, calcForoTotal } from './components/ForoSection';
import { InterpretacionSection, calcInterpretacionTotal } from './components/InterpretacionSection';
import { SonorizacionSection, calcSonorizacionTotal } from './components/SonorizacionSection';
import { HeaderBar } from './components/HeaderBar';
import { FooterSection } from './components/FooterSection';
import { useQuoteCalculator } from './hooks/useQuoteCalculator';
import { useAddonCalculations } from './hooks/useAddonCalculations';
import { useServiceCalculations } from './hooks/useServiceCalculations';
import { VideoCarouselModal } from './components/VideoCarouselModal';
import { ViubuxConnectSection } from './components/ViubuxConnectSection';
import { VoiceServicesSection, VOICE_CATEGORIES } from './components/VoiceServicesSection';
import { StudioFeesSection } from './components/StudioFeesSection';
import { HandFreeSection } from './components/HandFreeSection';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { MetaproduccionForm } from './components/MetaproduccionForm';
import { PaymentSuccess } from './components/PaymentSuccess';
import { FloatingContact } from './components/FloatingContact';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsConditions from './pages/TermsConditions';

import { AuthProvider, useAuth } from './contexts/AuthContext';
import LoginPage from './pages/LoginPage';
import { Loader2 } from 'lucide-react';

import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VIUBUX_LOGO = 'https://customer-assets.emergentagent.com/job_libro-quote-gen/artifacts/pf9umyn8_5.png';
const VIUBUX_ISO = 'https://customer-assets.emergentagent.com/job_libro-quote-gen/artifacts/pf9umyn8_5.png';

function Dashboard() {
  // Customer data state
  const [customerData, setCustomerData] = useState({
    name: '',
    phone: '',
    email: ''
  });
  const [projectName, setProjectName] = useState('');
  const [projectCoverUrl, setProjectCoverUrl] = useState('');
  
  // Terms accepted state
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsHighlight, setTermsHighlight] = useState(false);
  
  // Audiobook state
  const [formData, setFormData] = useState({
    bookTitle: '',
    authorName: '',
    copyright: '',
    wordCount: 0,
    pageCount: 0,
    coverUrl: '',
    coverFile: null,
    manualDuration: 0
  });
  const [selectedPackage, setSelectedPackage] = useState('essential');
  const [voiceCloning, setVoiceCloning] = useState(false);
  const [talentoPremium, setTalentoPremium] = useState(false);
  const [extraVoicesUltra, setExtraVoicesUltra] = useState(0);
  const [revisionAudio, setRevisionAudio] = useState('rev_none');
  const [masterizacion, setMasterizacion] = useState('master_none');
  const [multivozActive, setMultivozActive] = useState(false);
  const [multivozVoces, setMultivozVoces] = useState(1);
  const [manualDiscount, setManualDiscount] = useState(0);
  const [customDiscount, setCustomDiscount] = useState(0);
  const [customDiscountType, setCustomDiscountType] = useState('percent');
  const [showDiscounts, setShowDiscounts] = useState(false);
  const [selectedDistribution, setSelectedDistribution] = useState('dist_none');
  const [selectedBooktrailer, setSelectedBooktrailer] = useState('bt_none');
  
  // Podcast state
  const [selectedPodcastPackage, setSelectedPodcastPackage] = useState(null);
  const [selectedPodcastAddons, setSelectedPodcastAddons] = useState([]);
  const [podcastQuantity, setPodcastQuantity] = useState(1);
  const [podcastData, setPodcastData] = useState({ title: '', episodeDuration: 0 });
  const [podcastPostProduction, setPodcastPostProduction] = useState(false);

  // New sections state
  const [rentaEquipoOpen, setRentaEquipoOpen] = useState(false);
  const [rentaEquipoData, setRentaEquipoData] = useState({});
  const [transmisionOpen, setTransmisionOpen] = useState(false);
  const [transmisionData, setTransmisionData] = useState({});
  const [sonidoDirectoOpen, setSonidoDirectoOpen] = useState(false);
  const [sonidoDirectoData, setSonidoDirectoData] = useState({});
  const [foroOpen, setForoOpen] = useState(false);
  const [foroData, setForoData] = useState({});
  const [interpretacionOpen, setInterpretacionOpen] = useState(false);
  const [interpretacionData, setInterpretacionData] = useState({});
  const [sonorizacionOpen, setSonorizacionOpen] = useState(false);
  const [sonorizacionData, setSonorizacionData] = useState({});
  
  // Meditaciones Guiadas state
  const [selectedMeditacionesPackage, setSelectedMeditacionesPackage] = useState(null);
  const [selectedMeditacionesAddons, setSelectedMeditacionesAddons] = useState([]);
  const [meditacionesQuantity, setMeditacionesQuantity] = useState(1);
  const [meditacionesData, setMeditacionesData] = useState({
    title: '',
    episodeDuration: 0
  });
  const [meditacionesOpen, setMeditacionesOpen] = useState(false);

  // AudioCursos state
  const [selectedAudiocursosPackage, setSelectedAudiocursosPackage] = useState(null);
  const [selectedAudiocursosAddons, setSelectedAudiocursosAddons] = useState([]);
  const [audiocursosQuantity, setAudiocursosQuantity] = useState(1);
  const [audiocursosData, setAudiocursosData] = useState({
    title: '',
    episodeDuration: 0
  });
  const [audiocursosOpen, setAudiocursosOpen] = useState(false);

  // Walking Podcast state
  const [walkingPodcastOpen, setWalkingPodcastOpen] = useState(false);
  const [selectedWalkingPodcastPackage, setSelectedWalkingPodcastPackage] = useState(null);
  const [walkingPodcastQuantity, setWalkingPodcastQuantity] = useState(1);
  const [walkingPodcastData, setWalkingPodcastData] = useState({ title: '', episodeDuration: 0 });
  const [walkingSonidoDirectoDays, setWalkingSonidoDirectoDays] = useState(0);
  const [walkingAsistenteDays, setWalkingAsistenteDays] = useState(0);

  // Package details modal
  const [showPackageDetails, setShowPackageDetails] = useState(false);

  // Video carousel state
  const [showVideoModal, setShowVideoModal] = useState(false);

  // ViubuxConnect state
  const [viubuxConnectOpen, setViubuxConnectOpen] = useState(false);
  const [viubuxConnectActive, setViubuxConnectActive] = useState(false);
  const [vcCatalogoActive, setVcCatalogoActive] = useState(false);
  const [showDiscountPerItem, setShowDiscountPerItem] = useState(false);
  
  // Voice Services state
  const [voiceServicesOpen, setVoiceServicesOpen] = useState(false);
  const [voiceServicesQuantities, setVoiceServicesQuantities] = useState({});
  
  // Studio Fees state
  const [studioFeesOpen, setStudioFeesOpen] = useState(false);
  const [studioHours, setStudioHours] = useState({ premium: 0, essential: 0 });
  const [audioPostHours, setAudioPostHours] = useState(0);
  
  // Hand-Free state
  const [handFreeOpen, setHandFreeOpen] = useState(false);
  const [handFreeRows, setHandFreeRows] = useState([]);
  
  // Co-Inversión user opt-in (only shown when admin enables co-producción)
  const [coproduccionUserEnabled, setCoproduccionUserEnabled] = useState(false);
  
  // Invoice (Facturación) state
  const [invoiceRequested, setInvoiceRequested] = useState(false);
  const [invoiceFiscalData, setInvoiceFiscalData] = useState({
    receptor_rfc: '',
    receptor_nombre: '',
    receptor_regimen: '612',
    receptor_cp: '',
    receptor_uso_cfdi: 'G03',
    forma_pago: '03',
    metodo_pago: 'PUE'
  });
  
  // Servicios Adicionales state
  const [serviciosOpen, setServiciosOpen] = useState(true);
  
  // Legal Kit and Cover Design state (now as simple selections)
  const [selectedLegalKit, setSelectedLegalKit] = useState('legal_none');
  const [selectedCoverDesign, setSelectedCoverDesign] = useState('cover_none');
  const [selectedBrandingPack, setSelectedBrandingPack] = useState('branding_none');
  const [selectedMusicStock, setSelectedMusicStock] = useState('music_none');
  const [musicStockQuantity, setMusicStockQuantity] = useState(1);
  const [selectedConsultoria, setSelectedConsultoria] = useState('consultoria_none');
  const [consultoriaQuantity, setConsultoriaQuantity] = useState(1);
  
  // New additional services state
  const [selectedQrDinamico, setSelectedQrDinamico] = useState('qr_none');
  const [selectedEnlaceGo, setSelectedEnlaceGo] = useState('enlace_none');
  const [selectedNfcVcard, setSelectedNfcVcard] = useState('nfc_none');
  const [selectedSeparadores, setSelectedSeparadores] = useState('sep_none');
  const [selectedPreventa, setSelectedPreventa] = useState('preventa_none');
  const [selectedBannerPromotion, setSelectedBannerPromotion] = useState('banner_none');
  const [bannerPromotionMonths, setBannerPromotionMonths] = useState(1);
  const [selectedDisenoWebBanner, setSelectedDisenoWebBanner] = useState('web_banner_none');
  const [selectedPhotoshoot, setSelectedPhotoshoot] = useState('photoshoot_none');
  const [selectedPaginaAutor, setSelectedPaginaAutor] = useState('pagina_autor_none');
  const [selectedPautajeDigital, setSelectedPautajeDigital] = useState('pautaje_none');
  const [selectedCommissionFree, setSelectedCommissionFree] = useState('commission_free_none');
  const [selectedTarjetas, setSelectedTarjetas] = useState('tarjetas_none');
  const [selectedSeparadoresImpresos, setSelectedSeparadoresImpresos] = useState('sep_impresos_none');
  const [selectedEnvio, setSelectedEnvio] = useState('envio_none');
  
  // Editorial Services state
  const [editorialOpen, setEditorialOpen] = useState(false);
  const [selectedMaquetacion, setSelectedMaquetacion] = useState(false);
  const [selectedCorreccion, setSelectedCorreccion] = useState(false);
  const [correccionCuartillas, setCorreccionCuartillas] = useState(0);
  const [selectedPortadaFisica, setSelectedPortadaFisica] = useState(false);
  const [selectedImpresion, setSelectedImpresion] = useState(false);
  const [impresionCantidad, setImpresionCantidad] = useState(0);
  const [selectedGhostWriting, setSelectedGhostWriting] = useState(false);

  // PVP (Precio de Venta al Público) for break-even calculation
  const [pvp, setPvp] = useState(197);
  const [salesGoal, setSalesGoal] = useState(0); // Copies the user wants to sell
  
  // Section visibility state
  const [audiobookOpen, setAudiobookOpen] = useState(true);
  const [podcastOpen, setPodcastOpen] = useState(false);
  
  // Sections activated by Mr. Edux (session-only overrides)
  const [activatedSections, setActivatedSections] = useState(new Set());

  // UI state
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  // Recovery code state
  const [recoveryCode, setRecoveryCode] = useState('');
  const [savedQuoteCode, setSavedQuoteCode] = useState('');
  const [isRecovering, setIsRecovering] = useState(false);
  const [showRecoveryInput, setShowRecoveryInput] = useState(false);
  const [lastSavedFingerprint, setLastSavedFingerprint] = useState('');
  // Referral (from Connect)
  const [referrerCode, setReferrerCode] = useState('');
  const [referrerName, setReferrerName] = useState('');
  const [referrerUserId, setReferrerUserId] = useState('');
  const [showReferralInput, setShowReferralInput] = useState(false);
  const [referralInputValue, setReferralInputValue] = useState('');
  const [isVerifyingReferral, setIsVerifyingReferral] = useState(false);

  const verifyReferral = async (code) => {
    const clean = (code || '').trim();
    if (!clean) return;
    setIsVerifyingReferral(true);
    try {
      const res = await axios.get(`${API}/referrals/verify/${encodeURIComponent(clean)}`);
      if (res.data && res.data.valid) {
        setReferrerCode(clean);
        setReferrerName(res.data.full_name || res.data.username || clean);
        setReferrerUserId(res.data.user_id || '');
        setShowReferralInput(false);
        setReferralInputValue('');
        toast.success(`Referido: ${res.data.full_name || res.data.username || clean}`);
      } else {
        const msg = res.data?.message || '';
        if (/no disponible|not configured/i.test(msg)) {
          toast.error('Servicio de referidos no disponible. Verifica que CONNECT_API_URL esté configurado.');
        } else if (/no se encontró|not found/i.test(msg)) {
          toast.error('No se encontró un autor con ese usuario o correo');
        } else {
          toast.error(msg || 'Código de referido no encontrado');
        }
      }
    } catch (e) {
      toast.error('No se pudo verificar el código de referido');
    } finally {
      setIsVerifyingReferral(false);
    }
  };

  const clearReferral = () => {
    setReferrerCode('');
    setReferrerName('');
    setReferrerUserId('');
    setReferralInputValue('');
    setShowReferralInput(false);
  };
  
  const quotePreviewRef = useRef(null);
  
  const {
    settings,
    setSettings,
    currency,
    setCurrency,
    toggleCurrency,
    calculateQuote,
    formatPrice,
    formatDuration,
    packages,
    manualDiscounts
  } = useQuoteCalculator();

  // Fetch settings from backend
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/settings`);
        setSettings(response.data);
      } catch (error) {
        console.error('Error fetching settings:', error);
        toast.error('Error al cargar la configuración');
      } finally {
        setIsLoading(false);
      }
    };
    fetchSettings();
  }, [setSettings]);

  // Auto-detect currency based on IP geolocation
  useEffect(() => {
    const detectCurrency = async () => {
      try {
        const response = await axios.get(`${API}/geo/currency`, { timeout: 5000 });
        if (response.data?.currency) {
          setCurrency(response.data.currency);
        }
      } catch (e) {
        // Silent fail - default currency stays
      }
    };
    detectCurrency();
  }, [setCurrency]);

  // Auto-recover quote from URL params (?code=VBX-XXXXXX)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    if (code && code.startsWith('VBX-')) {
      setRecoveryCode(code);
      // Delay to ensure settings are loaded
      const timer = setTimeout(async () => {
        try {
          const response = await axios.get(`${API}/quotes/recover/${code}`);
          handleLoadQuote(response.data);
          setSavedQuoteCode(response.data.recovery_code || code);
          toast.success(`Cotización ${code} recuperada`);
          // Clean URL
          window.history.replaceState({}, '', window.location.pathname);
        } catch {
          toast.error('Código de cotización no encontrado');
        }
      }, 1500);
      return () => clearTimeout(timer);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Calculate audiobook quote
  // If manual duration is provided, use that instead of calculating from words/pages
  let audiobookQuote = null;
  if (selectedPackage && formData.manualDuration > 0) {
    // Use manual duration - calculate directly
    const pkg = settings.packages?.find(p => p.id === selectedPackage);
    if (pkg) {
      const durationHours = formData.manualDuration;
      const basePrice = pkg.base_price;
      const volumeDiscountPercent = durationHours >= 11 ? 30 : durationHours >= 9 ? 20 : durationHours >= 7 ? 15 : durationHours >= 5 ? 10 : 0;
      const priceAfterVolume = basePrice * (1 - volumeDiscountPercent / 100);
      const manualDiscountAmount = priceAfterVolume * (manualDiscount / 100);
      const finalPrice = priceAfterVolume - manualDiscountAmount;
      const grandTotal = finalPrice * durationHours;
      
      audiobookQuote = {
        estimatedDurationHours: durationHours,
        estimatedDurationMinutes: durationHours * 60,
        basePrice: basePrice,
        volumeDiscountPercent: volumeDiscountPercent,
        volumeDiscountAmount: basePrice * (volumeDiscountPercent / 100) * durationHours,
        manualDiscountPercent: manualDiscount,
        manualDiscountAmount: manualDiscountAmount * durationHours,
        finalPrice: finalPrice,
        grandTotal: grandTotal,
        totalSavings: (basePrice - finalPrice) * durationHours,
        package: pkg
      };
    }
  } else if (selectedPackage) {
    // Calculate from words/pages
    audiobookQuote = calculateQuote(
      formData.wordCount,
      formData.pageCount,
      selectedPackage,
      manualDiscount
    );
  }

  // Calculate audiobook addons using dynamic prices from settings (extracted to hook)
  const addonState = {
    selectedDistribution, selectedBooktrailer, selectedLegalKit, selectedCoverDesign,
    selectedBrandingPack, selectedMusicStock, musicStockQuantity,
    selectedConsultoria, consultoriaQuantity,
    selectedQrDinamico, selectedEnlaceGo, selectedNfcVcard, selectedSeparadores,
    selectedPreventa, selectedBannerPromotion, bannerPromotionMonths,
    selectedDisenoWebBanner, selectedPhotoshoot, selectedPaginaAutor,
    selectedPautajeDigital,
    selectedCommissionFree,
    selectedTarjetas, selectedSeparadoresImpresos, selectedEnvio
  };
  const {
    distributionAddon, booktrailerAddon, legalKitAddon, coverDesignAddon,
    brandingPackAddon, musicStockAddon, consultoriaAddon,
    qrDinamicoAddon, enlaceGoAddon, nfcVcardAddon, separadoresAddon,
    preventaAddon, bannerPromotionAddon, disenoWebBannerAddon,
    photoshootAddon, paginaAutorAddon, pautajeDigitalAddon, commissionFreeAddon,
    tarjetasAddon, separadoresImpresosAddon, envioAddon,
    audiobookAddonsTotal
  } = useAddonCalculations(settings, addonState);

  // Calculate podcast, meditaciones, audiocursos totals (extracted to hook)
  const serviceState = {
    selectedPodcastPackage, selectedPodcastAddons, podcastQuantity, podcastData,
    podcastPostProduction,
    selectedMeditacionesPackage, selectedMeditacionesAddons, meditacionesQuantity, meditacionesData,
    selectedAudiocursosPackage, selectedAudiocursosAddons, audiocursosQuantity, audiocursosData
  };
  const {
    selectedPodcastPkg, podcastAddonsSubtotal, estimatedRecordingHours, podcastTotal,
    meditacionesEstimatedHours, meditacionesTotal,
    audiocursosEstimatedHours, audiocursosTotal
  } = useServiceCalculations(settings, serviceState);

  // Check if audiobook has valid data (words or pages > 0 OR manual duration) AND a package is selected
  const hasValidAudiobookData = selectedPackage && (formData.wordCount > 0 || formData.pageCount > 0 || formData.manualDuration > 0);

  // Helper functions for service package prices (used in QuotePreview props)
  const getMeditacionesPackagePrice = (pkgId) => {
    const prices = { 'med_essential': settings.price_med_essential || 1997, 'med_platino': settings.price_med_platino || 3997, 'med_premium': settings.price_med_premium || 6997 };
    return prices[pkgId] || 0;
  };
  const getAudiocursosPackagePrice = (pkgId) => {
    const prices = { 'ac_essential': settings.price_ac_essential || 997, 'ac_platino': settings.price_ac_platino || 3500, 'ac_premium': settings.price_ac_premium || 5500 };
    return prices[pkgId] || 0;
  };
  
  // Use manual duration if provided, otherwise use calculated
  const effectiveDurationHours = formData.manualDuration > 0 
    ? formData.manualDuration 
    : (audiobookQuote?.estimatedDurationHours || 0);
  
  // Check if any addon is selected (independent of audiobook)
  const hasSelectedAddons = audiobookAddonsTotal > 0;
  
  // Calculate grand total (audiobook + podcast + walking podcast + meditaciones + audiocursos + addons)
  // Audiobook production cost only if there's valid data
  const audiobookProductionCost = hasValidAudiobookData ? (audiobookQuote?.grandTotal || 0) : 0;
  const voiceCloningCost = (selectedPackage === 'ultra' && voiceCloning) ? 7997 : 0;
  const talentoPremiumPricePerHour = settings.price_talento_premium || 1500;
  const talentoPremiumCost = talentoPremium ? talentoPremiumPricePerHour * (audiobookQuote?.estimatedDurationHours || 0) : 0;
  const extraVoicesUltraCost = (selectedPackage === 'ultra') ? extraVoicesUltra * 697 : 0;
  const revisionAudioCost = revisionAudio === 'rev_7' ? 697 : revisionAudio === 'rev_10' ? 997 : revisionAudio === 'rev_20' ? 1497 : 0;
  const masterizacionCost = masterizacion === 'master_3' ? 1000 : masterizacion === 'master_7' ? 1500 : masterizacion === 'master_10' ? 1850 : masterizacion === 'master_20' ? 2500 : 0;
  const audiobookDurationHours = audiobookQuote?.estimatedDurationHours || (formData.manualDuration > 0 ? formData.manualDuration : 0);
  const multivozCost = multivozActive ? 997 + (multivozVoces * 50 * audiobookDurationHours * 60) : 0;
  
  // Walking Podcast calculation
  const walkingPodcastPkg = selectedWalkingPodcastPackage ? (() => {
    const prices = { 'wpod_essential': settings.price_wpod_essential || 997, 'wpod_platino': settings.price_wpod_platino || 3500, 'wpod_premium': settings.price_wpod_premium || 5500 };
    return prices[selectedWalkingPodcastPackage] || 0;
  })() : 0;
  const walkingEstimatedHours = walkingPodcastData.episodeDuration 
    ? (walkingPodcastQuantity * walkingPodcastData.episodeDuration * 2.5) / 60 
    : 0;
  const walkingSonidoTotal = walkingSonidoDirectoDays * (settings.price_wpod_sonido_directo || 5500);
  const walkingAsistenteTotal = walkingAsistenteDays * (settings.price_wpod_asistente || 1000);
  const walkingExtrasTotal = walkingSonidoTotal + walkingAsistenteTotal;
  const walkingPodcastTotal = walkingPodcastPkg 
    ? (walkingEstimatedHours > 0 
        ? (walkingPodcastPkg * walkingEstimatedHours) + walkingExtrasTotal
        : (walkingPodcastPkg * walkingPodcastQuantity) + walkingExtrasTotal)
    : walkingExtrasTotal;

  // Addons are always independent and added to total
  const rentaEquipoTotal = calcRentaEquipoTotal(rentaEquipoData, settings);
  const transmisionTotal = calcTransmisionTotal(transmisionData, settings);
  const sonidoDirectoTotal = calcSonidoDirectoTotal(sonidoDirectoData, settings);
  const foroTotal = calcForoTotal(foroData, settings);
  const interpretacionTotal = calcInterpretacionTotal(interpretacionData, settings);
  const sonorizacionTotal = calcSonorizacionTotal(sonorizacionData, settings);
  const subtotalBeforeEditorial = audiobookProductionCost + voiceCloningCost + talentoPremiumCost + extraVoicesUltraCost + revisionAudioCost + masterizacionCost + multivozCost + audiobookAddonsTotal + podcastTotal + walkingPodcastTotal + meditacionesTotal + audiocursosTotal + rentaEquipoTotal + transmisionTotal + sonidoDirectoTotal + foroTotal + interpretacionTotal + sonorizacionTotal;
  
  // Editorial Services subtotal
  const editorialMaquetacionTotal = selectedMaquetacion ? (settings.price_editorial_maquetacion || 10000) : 0;
  const editorialCorreccionTotal = selectedCorreccion ? (settings.price_editorial_correccion || 97) * (correccionCuartillas || 0) : 0;
  const editorialPortadaFisicaTotal = selectedPortadaFisica ? (settings.price_editorial_portada_fisica || 8500) : 0;
  const editorialImpresionPricePerUnit = impresionCantidad >= 251 ? (settings.price_editorial_impresion_251 || 83) : impresionCantidad >= 101 ? (settings.price_editorial_impresion_101 || 97) : (settings.price_editorial_impresion || 180);
  const editorialImpresionTotal = selectedImpresion ? editorialImpresionPricePerUnit * (impresionCantidad || 0) : 0;
  const editorialGhostWritingTotal = selectedGhostWriting ? (settings.price_editorial_ghost_writing || 287000) : 0;
  const editorialSubtotal = editorialMaquetacionTotal + editorialCorreccionTotal + editorialPortadaFisicaTotal + editorialImpresionTotal + editorialGhostWritingTotal;
  
  const subtotalBeforeCustomDiscount = subtotalBeforeEditorial + editorialSubtotal;
  
  // Voice Services total
  const voiceServicesTotal = useMemo(() => {
    let total = 0;
    VOICE_CATEGORIES.forEach(cat => {
      cat.items.forEach(item => {
        const qty = voiceServicesQuantities[item.id] || 0;
        if (qty > 0) total += item.price * qty;
      });
    });
    return total;
  }, [voiceServicesQuantities]);

  // Studio Fees total
  const studioFeesTotal = useMemo(() => {
    const premiumPrice = settings.price_studio_premium || 3500;
    const essentialPrice = settings.price_studio_essential || 2000;
    const audioPostPrice = settings.price_audio_post || 1500;
    return (studioHours.premium || 0) * premiumPrice + (studioHours.essential || 0) * essentialPrice + (audioPostHours || 0) * audioPostPrice;
  }, [studioHours, audioPostHours, settings]);

  // Hand-Free total
  const handFreeTotal = useMemo(() => {
    return handFreeRows.reduce((sum, r) => sum + (r.unitPrice || 0) * (r.quantity || 0), 0);
  }, [handFreeRows]);

  
  // ViubuxConnect: inscription price inflated so after 15% discount it nets to $4,997
  // Base price = 4997 / 0.85 = 5,879
  const viubuxConnectBasePrice = settings.price_viubux_connect || 5879;
  const viubuxConnectActive_ = viubuxConnectActive;
  const viubuxConnectInscription = viubuxConnectActive_ ? viubuxConnectBasePrice : 0;
  const vcCatalogPrice = settings.price_vc_catalogo || 2248;
  const vcCatalogoCost = vcCatalogoActive ? vcCatalogPrice : 0;
  const subtotalWithConnect = subtotalBeforeCustomDiscount + voiceServicesTotal + studioFeesTotal + handFreeTotal + viubuxConnectInscription + vcCatalogoCost;
  const viubuxConnectDiscountPercent = viubuxConnectActive_ ? 15 : 0;
  const viubuxConnectDiscountAmount = subtotalWithConnect * (viubuxConnectDiscountPercent / 100);
  const subtotalAfterConnect = subtotalWithConnect - viubuxConnectDiscountAmount;
  
  const customDiscountAmount = customDiscountType === 'fixed' ? customDiscount : subtotalAfterConnect * (customDiscount / 100);
  const subtotalAfterDiscount = subtotalAfterConnect - customDiscountAmount;
  
  // Calculate IVA (16%)
  const ivaAmount = subtotalAfterDiscount * 0.16;
  const finalTotalWithIVA = subtotalAfterDiscount + ivaAmount;
  
  // Keep backward compatibility - finalTotalWithCustomDiscount now includes IVA
  const finalTotalWithCustomDiscount = finalTotalWithIVA;

  // Co-Producción calculation - user can opt-in when admin enables it
  const coproduccionAvailable = settings.show_coproduccion === true;
  const showCoproduccion = coproduccionAvailable && coproduccionUserEnabled;
  const coproduccionPercent = settings.coproduccion_percent || 50;
  const coproduccionAmount = showCoproduccion ? finalTotalWithCustomDiscount * (coproduccionPercent / 100) : 0;
  const clientPayAmount = finalTotalWithCustomDiscount - coproduccionAmount;

  const handleFormChange = useCallback((updates) => {
    setFormData(prev => ({ ...prev, ...updates }));
  }, []);

  // Auto-fill handler for Mr. Edux AI chat
  const handleAutoFill = useCallback((data) => {
    // Customer data
    if (data.name) setCustomerData(prev => ({ ...prev, name: data.name }));
    if (data.phone) setCustomerData(prev => ({ ...prev, phone: data.phone }));
    if (data.email) setCustomerData(prev => ({ ...prev, email: data.email }));
    
    // Book data (Audiobook)
    const wordCount = parseInt(data.wordCount) || 0;
    const pageCount = parseInt(data.pageCount) || 0;
    
    let calculatedDuration = 0;
    if (wordCount > 0) {
      calculatedDuration = Math.round((wordCount / 9300) * 10) / 10;
    } else if (pageCount > 0) {
      calculatedDuration = Math.round(((pageCount * 250) / 9300) * 10) / 10;
    }
    if (calculatedDuration > 0 && calculatedDuration < 1) calculatedDuration = 1;
    
    setFormData(prev => ({
      ...prev,
      bookTitle: data.bookTitle || prev.bookTitle,
      wordCount: wordCount || prev.wordCount,
      pageCount: pageCount || prev.pageCount,
      manualDuration: calculatedDuration > 0 ? calculatedDuration : prev.manualDuration
    }));
    
    // Select package and open audiobook section
    if (data.package) {
      setSelectedPackage(data.package);
      setAudiobookOpen(true);
    }

    // Podcast
    if (data.podcast) {
      const p = data.podcast;
      if (p.package) setSelectedPodcastPackage(p.package);
      if (p.quantity) setPodcastQuantity(parseInt(p.quantity) || 1);
      if (p.episodeDuration) setPodcastData(prev => ({ ...prev, episodeDuration: parseFloat(p.episodeDuration) || 0 }));
      if (p.title) setPodcastData(prev => ({ ...prev, title: p.title }));
      if (p.postProduction) setPodcastPostProduction(true);
      setPodcastOpen(true);
    }

    // Sonorización
    if (data.sonorizacion) {
      const s = data.sonorizacion;
      setSonorizacionData({ package: s.package || '', dias: parseInt(s.dias) || 0 });
      setSonorizacionOpen(true);
    }

    // Interpretación
    if (data.interpretacion) {
      const i = data.interpretacion;
      setInterpretacionData({
        horas: parseInt(i.horas) || 0,
        dias: parseInt(i.dias) || 0,
        cabina: i.cabina || false,
        cabinaDias: parseInt(i.cabinaDias) || 0,
        equipoTxQty: parseInt(i.equipoTxQty) || 0,
        equipoTxDias: parseInt(i.equipoTxDias) || 0,
        tecnicoDias: parseInt(i.tecnicoDias) || 0,
      });
      setInterpretacionOpen(true);
    }

    // Renta de Equipo
    if (data.renta_equipo) {
      const r = data.renta_equipo;
      setRentaEquipoData({
        microfonoQty: parseInt(r.microfonoQty) || 0,
        microfonoDias: parseInt(r.microfonoDias) || 0,
        camaraDias: parseInt(r.camaraDias) || 0,
        lavalierQty: parseInt(r.lavalierQty) || 0,
        lavalierDias: parseInt(r.lavalierDias) || 0,
        teleprompterDias: parseInt(r.teleprompterDias) || 0,
      });
      setRentaEquipoOpen(true);
    }

    // Transmisión en Vivo
    if (data.transmision) {
      const t = data.transmision;
      setTransmisionData({
        basicQty: parseInt(t.basicQty) || 0,
        proDias: parseInt(t.proDias) || 0,
      });
      setTransmisionOpen(true);
    }

    // Sonido Directo
    if (data.sonido_directo) {
      const sd = data.sonido_directo;
      setSonidoDirectoData({
        basicProHoras: parseInt(sd.basicProHoras) || 0,
        proHoras: parseInt(sd.proHoras) || 0,
      });
      setSonidoDirectoOpen(true);
    }

    // Foro
    if (data.foro) {
      const f = data.foro;
      setForoData({
        santaFeHoras: parseInt(f.santaFeHoras) || 0,
        masivoDias: parseInt(f.masivoDias) || 0,
        storageDias: parseInt(f.storageDias) || 0,
      });
      setForoOpen(true);
    }
    
    toast.success('Mr. Edux está llenando el cotizador...', { duration: 3000 });
  }, []);

  // Handle section activation from Mr. Edux
  const handleActivateSections = useCallback((sections) => {
    const sectionMap = {
      audiobook: { open: setAudiobookOpen },
      podcast: { open: setPodcastOpen },
      walking_podcast: { open: setWalkingPodcastOpen },
      meditaciones: { open: setMeditacionesOpen },
      audiocursos: { open: setAudiocursosOpen },
      servicios_adicionales: { open: setServiciosOpen },
      editorial: { open: setEditorialOpen },
      renta_equipo: { open: setRentaEquipoOpen },
      transmision: { open: setTransmisionOpen },
      sonido_directo: { open: setSonidoDirectoOpen },
      foro: { open: setForoOpen },
      interpretacion: { open: setInterpretacionOpen },
      sonorizacion: { open: setSonorizacionOpen },
      viubux_connect: { open: setViubuxConnectOpen },
    };

    setActivatedSections(prev => {
      const next = new Set(prev);
      sections.forEach(s => next.add(s));
      return next;
    });

    sections.forEach(sectionKey => {
      const mapping = sectionMap[sectionKey];
      if (mapping) {
        mapping.open(true);
      }
    });

    toast.success('Mr. Edux activó secciones en el cotizador', { duration: 2000 });
  }, []);

  const handlePodcastAddonToggle = (addonId) => {
    setSelectedPodcastAddons(prev => 
      prev.includes(addonId)
        ? prev.filter(id => id !== addonId)
        : [...prev, addonId]
    );
  };

  const handleSaveSettings = async (newSettings, password) => {
    try {
      // Send all settings to backend dynamically (avoid manually listing fields)
      const { id, _id, updated_at, ...settingsToSend } = newSettings;
      const response = await axios.put(`${API}/settings?password=${encodeURIComponent(password)}`, settingsToSend);
      setSettings(response.data);
      toast.success('Configuración guardada correctamente');
    } catch (error) {
      console.error('Error saving settings:', error);
      if (error.response?.status === 401) {
        toast.error('Contraseña de administrador incorrecta');
      } else {
        toast.error('Error al guardar la configuración');
      }
      throw error;
    }
  };

  // Generate a fingerprint of the current quote state to detect changes
  const getQuoteFingerprint = () => {
    return JSON.stringify({
      proj: projectName, c: customerData, f: formData, pkg: selectedPackage,
      pod: selectedPodcastPackage, podQ: podcastQuantity, podA: selectedPodcastAddons,
      wpod: selectedWalkingPodcastPackage, wpodQ: walkingPodcastQuantity, wpodSD: walkingSonidoDirectoDays, wpodA: walkingAsistenteDays,
      med: selectedMeditacionesPackage, medQ: meditacionesQuantity,
      ac: selectedAudiocursosPackage, acQ: audiocursosQuantity,
      addons: [selectedDistribution, selectedBooktrailer, selectedLegalKit, selectedCoverDesign,
        selectedBrandingPack, selectedMusicStock, selectedConsultoria,
        selectedQrDinamico, selectedEnlaceGo, selectedNfcVcard, selectedSeparadores,
        selectedPreventa, selectedBannerPromotion, selectedDisenoWebBanner,
        selectedPhotoshoot, selectedPaginaAutor, selectedPautajeDigital, selectedCommissionFree,
        selectedTarjetas, selectedSeparadoresImpresos, selectedEnvio],
      audio: [revisionAudio, masterizacion, voiceCloning, talentoPremium, extraVoicesUltra, multivozActive, multivozVoces],
      voiceServices: [voiceServicesQuantities],
      studio: [studioHours, audioPostHours],
      handFree: [handFreeRows],
      editorial: [selectedMaquetacion, selectedCorreccion, correccionCuartillas, selectedPortadaFisica, selectedImpresion, impresionCantidad, selectedGhostWriting],
      renta: rentaEquipoData, transmision: transmisionData, sonido: sonidoDirectoData, foro: foroData, interp: interpretacionData, sono: sonorizacionData,
      podPP: podcastPostProduction,
      vc: viubuxConnectActive, coprod: coproduccionUserEnabled,
      disc: customDiscount, curr: currency
    });
  };

  const handleSaveQuote = async () => {
    // Check if anything changed since last save
    const currentFingerprint = getQuoteFingerprint();
    if (savedQuoteCode && currentFingerprint === lastSavedFingerprint) {
      toast.info('La cotización no ha cambiado desde la última vez que se guardó.');
      return true; // Return true so payment flow continues
    }

    // Validation
    const errors = [];
    if (!customerData.name || customerData.name.trim() === '') {
      errors.push('El nombre del cliente es obligatorio');
    }
    if (!customerData.email || customerData.email.trim() === '') {
      errors.push('El email del cliente es obligatorio');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customerData.email)) {
      errors.push('El email no tiene un formato válido');
    }
    if (!termsAccepted) {
      errors.push('Debes aceptar los términos y condiciones');
      setTermsHighlight(true);
      setTimeout(() => setTermsHighlight(false), 4500);
      document.getElementById('terms-checkbox-section')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    // Allow saving if there's audiobook, podcast, meditaciones, audiocursos, OR any addon selected
    const hasAudiobook = formData.bookTitle || formData.manualDuration > 0 || formData.wordCount > 0 || formData.pageCount > 0;
    const hasPodcast = selectedPodcastPackage !== null;
    const hasMeditaciones = selectedMeditacionesPackage !== null;
    const hasAudiocursos = selectedAudiocursosPackage !== null;
    const hasAddons = audiobookAddonsTotal > 0;
    const hasWalking = selectedWalkingPodcastPackage !== null;
    const hasEditorial = editorialSubtotal > 0;
    const hasRenta = rentaEquipoTotal > 0;
    const hasTransmision = transmisionTotal > 0;
    const hasSonido = sonidoDirectoTotal > 0;
    const hasForo = foroTotal > 0;
    const hasInterp = interpretacionTotal > 0;
    const hasSono = sonorizacionTotal > 0;
    
    const hasVoiceServices = voiceServicesTotal > 0;
    const hasStudioFees = studioFeesTotal > 0;
    const hasHandFree = handFreeTotal > 0;
    if (!hasAudiobook && !hasPodcast && !hasMeditaciones && !hasAudiocursos && !hasAddons && !hasWalking && !hasEditorial && !hasRenta && !hasTransmision && !hasSonido && !hasForo && !hasInterp && !hasSono && !hasVoiceServices && !hasStudioFees && !hasHandFree) {
      errors.push('Selecciona al menos un servicio');
    }
    
    if (errors.length > 0) {
      errors.forEach(error => toast.error(error));
      return false;
    }

    try {
      const response = await axios.post(`${API}/quotes`, {
        // Customer info
        customer_name: customerData.name,
        customer_phone: customerData.phone,
        customer_email: customerData.email,
        
        // Audiobook info
        author_name: formData.authorName,
        book_title: formData.bookTitle,
        word_count: formData.wordCount,
        page_count: formData.pageCount,
        manual_duration: formData.manualDuration || 0,
        package_id: selectedPackage,
        voice_cloning: voiceCloning,
        cover_url: formData.coverUrl,
        manual_discount: manualDiscount,
        
        // Audiobook addons
        distribution_id: selectedDistribution,
        booktrailer_id: selectedBooktrailer,
        legal_kit_id: selectedLegalKit,
        cover_design_id: selectedCoverDesign,
        branding_pack_id: selectedBrandingPack,
        music_stock_id: selectedMusicStock,
        music_stock_quantity: musicStockQuantity,
        consultoria_id: selectedConsultoria === 'consultoria' ? 'consultoria' : null,
        consultoria_quantity: consultoriaQuantity,
        
        // New additional services
        qr_dinamico_id: selectedQrDinamico !== 'qr_none' ? selectedQrDinamico : null,
        enlace_go_id: selectedEnlaceGo !== 'enlace_none' ? selectedEnlaceGo : null,
        nfc_vcard_id: selectedNfcVcard !== 'nfc_none' ? selectedNfcVcard : null,
        separadores_id: selectedSeparadores !== 'sep_none' ? selectedSeparadores : null,
        preventa_id: selectedPreventa !== 'preventa_none' ? selectedPreventa : null,
        banner_promotion_id: selectedBannerPromotion !== 'banner_none' ? selectedBannerPromotion : null,
        banner_promotion_months: bannerPromotionMonths,
        diseno_web_banner_id: selectedDisenoWebBanner !== 'web_banner_none' ? selectedDisenoWebBanner : null,
        photoshoot_id: selectedPhotoshoot !== 'photoshoot_none' ? selectedPhotoshoot : null,
        pagina_autor_id: selectedPaginaAutor !== 'pagina_autor_none' ? selectedPaginaAutor : null,
        
        // Audio services
        revision_audio: revisionAudio !== 'rev_none' ? revisionAudio : null,
        masterizacion: masterizacion !== 'master_none' ? masterizacion : null,
        voice_cloning: voiceCloning,
        talento_premium: talentoPremium,
        extra_voices_ultra: extraVoicesUltra,
        
        // Imprenta addons
        tarjetas_id: selectedTarjetas !== 'tarjetas_none' ? selectedTarjetas : null,
        separadores_impresos_id: selectedSeparadoresImpresos !== 'sep_impresos_none' ? selectedSeparadoresImpresos : null,
        envio_id: selectedEnvio !== 'envio_none' ? selectedEnvio : null,
        
        // Project cover
        project_cover_url: projectCoverUrl || null,
        voice_services: voiceServicesTotal > 0 ? voiceServicesQuantities : null,
        studio_hours: studioFeesTotal > 0 ? studioHours : null,
        audio_post_hours: audioPostHours > 0 ? audioPostHours : null,
        hand_free_rows: handFreeTotal > 0 ? handFreeRows : null,
        // Referral
        referrer_code: referrerCode || null,
        referrer_name: referrerName || null,
        referrer_user_id: referrerUserId || null,
        
        // Podcast
        podcast_package_id: selectedPodcastPackage,
        podcast_addons: selectedPodcastAddons,
        podcast_quantity: podcastQuantity,
        podcast_title: podcastData.title || '',
        podcast_episode_duration: podcastData.episodeDuration || 0,
        podcast_estimated_hours: estimatedRecordingHours,
        
        // Meditaciones Guiadas
        meditaciones_package_id: selectedMeditacionesPackage,
        meditaciones_quantity: meditacionesQuantity,
        meditaciones_estimated_hours: meditacionesEstimatedHours,
        meditaciones_total: meditacionesTotal,
        
        // AudioCursos
        audiocursos_package_id: selectedAudiocursosPackage,
        audiocursos_quantity: audiocursosQuantity,
        audiocursos_estimated_hours: audiocursosEstimatedHours,
        audiocursos_total: audiocursosTotal,
        
        // Totals
        custom_discount: customDiscount,
        custom_discount_type: customDiscountType,
        grand_total: finalTotalWithCustomDiscount,
        currency: currency,
        exchange_rate: settings.usd_exchange_rate || 17.5,
        viubux_connect_active: viubuxConnectActive,
        vc_catalogo_active: vcCatalogoActive,
        
        // Co-Producción
        coproduccion_active: showCoproduccion,
        coproduccion_percent: coproduccionPercent,
        coproduccion_amount: coproduccionAmount,
        client_pay_amount: clientPayAmount,
        
        terms_accepted: termsAccepted,
        
        // Material URLs
        cover_url: formData.coverUrl || null,
        fiscal_url: customerData.fiscalUrl || null,
        book_pdf_url: formData.bookPdfUrl || null,
        
        // Invoice request
        invoice_requested: invoiceRequested,
        invoice_fiscal_data: invoiceRequested ? invoiceFiscalData : null,
        
        // Project name
        project_name: projectName || null,
        
        // Walking Podcast
        walking_podcast_package: selectedWalkingPodcastPackage,
        walking_podcast_quantity: walkingPodcastQuantity,
        walking_sonido_directo_days: walkingSonidoDirectoDays,
        walking_asistente_days: walkingAsistenteDays,
        
        // Podcast post-production
        podcast_post_production: podcastPostProduction,
        
        // New sections data (stored as JSON objects)
        renta_equipo_data: Object.keys(rentaEquipoData).length > 0 ? rentaEquipoData : null,
        transmision_data: Object.keys(transmisionData).length > 0 ? transmisionData : null,
        sonido_directo_data: Object.keys(sonidoDirectoData).length > 0 ? sonidoDirectoData : null,
        foro_data: Object.keys(foroData).length > 0 ? foroData : null,
        interpretacion_data: Object.keys(interpretacionData).length > 0 ? interpretacionData : null,
        sonorizacion_data: Object.keys(sonorizacionData).length > 0 ? sonorizacionData : null,
        
        // Pautaje Digital
        pautaje_digital_id: selectedPautajeDigital !== 'pautaje_none' ? selectedPautajeDigital : null,
        commission_free_id: selectedCommissionFree !== 'commission_free_none' ? selectedCommissionFree : null,
        
        // Editorial data
        editorial_data: editorialSubtotal > 0 ? {
          maquetacion: selectedMaquetacion,
          correccion: selectedCorreccion,
          correccionCuartillas: correccionCuartillas,
          portadaFisica: selectedPortadaFisica,
          impresion: selectedImpresion,
          impresionCantidad: impresionCantidad,
          ghostWriting: selectedGhostWriting
        } : null
      });
      
      // Show recovery code in a special toast
      const recoveryCode = response.data.recovery_code;
      if (recoveryCode) {
        setSavedQuoteCode(recoveryCode);
        setLastSavedFingerprint(getQuoteFingerprint());
        // Auto-copy to clipboard
        try {
          await navigator.clipboard.writeText(recoveryCode);
        } catch (e) {
          // Fallback for older browsers
          const textarea = document.createElement('textarea');
          textarea.value = recoveryCode;
          document.body.appendChild(textarea);
          textarea.select();
          document.execCommand('copy');
          document.body.removeChild(textarea);
        }
        toast.success(
          <div className="space-y-2">
            <p className="font-medium">Cotización guardada</p>
            <div className="bg-white/10 rounded p-2">
              <p className="text-xs text-slate-300">Tu código de recuperación:</p>
              <p className="text-lg font-mono font-bold text-[#aa2aff]">{recoveryCode}</p>
            </div>
            <p className="text-xs text-green-400">Copiado al portapapeles</p>
          </div>,
          { duration: 10000 }
        );
      } else {
        toast.success('Cotización guardada correctamente');
      }
      
      // Submit invoice request if enabled
      if (invoiceRequested && invoiceFiscalData?.receptor_rfc) {
        try {
          // Calculate amounts for invoice based on subtotal (before IVA) and IVA
          const invoiceSubtotal = subtotalAfterDiscount;
          const invoiceIva = ivaAmount;
          const invoiceTotal = finalTotalWithCustomDiscount;
          
          await axios.post(`${API}/invoicing/request`, {
            quote_id: response.data.id || null,
            recovery_code: recoveryCode || null,
            customer_name: customerData.name,
            customer_email: customerData.email,
            receptor_rfc: invoiceFiscalData.receptor_rfc,
            receptor_nombre: invoiceFiscalData.receptor_nombre,
            receptor_regimen: invoiceFiscalData.receptor_regimen,
            receptor_cp: invoiceFiscalData.receptor_cp,
            receptor_uso_cfdi: invoiceFiscalData.receptor_uso_cfdi,
            descripcion: `Servicios audiovisuales - ${formData.bookTitle || 'Cotización Viubux'}`,
            subtotal: invoiceSubtotal,
            iva_amount: invoiceIva,
            total: invoiceTotal,
            moneda: currency,
            forma_pago: invoiceFiscalData.forma_pago,
            metodo_pago: invoiceFiscalData.metodo_pago
          });
          toast.success('Solicitud de factura enviada. Será revisada por el administrador.');
        } catch (invoiceErr) {
          console.error('Error requesting invoice:', invoiceErr);
          toast.error('La cotización se guardó pero hubo un error al solicitar la factura.');
        }
      }
      
      return true;
    } catch (error) {
      console.error('Error saving quote:', error);
      toast.error('Error al guardar la cotización');
      return false;
    }
  };

  const handlePrint = () => {
    const printContent = quotePreviewRef.current;
    if (!printContent) {
      toast.error('No hay cotización para imprimir');
      return;
    }
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Cotización Viubux</title>
        <link href="https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700&display=swap" rel="stylesheet">
        <style>
          * { 
            box-sizing: border-box; 
            margin: 0; 
            padding: 0;
          }
          body { 
            font-family: 'Montserrat', Verdana, sans-serif; 
            margin: 0; 
            padding: 20px;
            background: white;
            color: #1e293b;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .print-container {
            max-width: 800px;
            margin: 0 auto;
            background: white;
            padding: 20px;
          }
          /* Header styles */
          .bg-gradient-to-r { background: linear-gradient(to right, #aa2aff, #272b96) !important; }
          .from-\\[\\#aa2aff\\] { background: linear-gradient(to right, #aa2aff, #272b96) !important; }
          .bg-\\[\\#0f133a\\] { background: #0f133a !important; }
          .text-white { color: white !important; }
          .text-slate-800 { color: #1e293b !important; }
          .text-slate-600 { color: #475569 !important; }
          .text-slate-500 { color: #64748b !important; }
          .text-slate-400 { color: #94a3b8 !important; }
          .text-\\[\\#aa2aff\\] { color: #aa2aff !important; }
          .font-bold { font-weight: 700 !important; }
          .font-semibold { font-weight: 600 !important; }
          .font-medium { font-weight: 500 !important; }
          .text-xs { font-size: 0.75rem !important; }
          .text-sm { font-size: 0.875rem !important; }
          .text-lg { font-size: 1.125rem !important; }
          .text-xl { font-size: 1.25rem !important; }
          .text-2xl { font-size: 1.5rem !important; }
          .text-3xl { font-size: 1.875rem !important; }
          .rounded-lg { border-radius: 0.5rem !important; }
          .rounded-xl { border-radius: 0.75rem !important; }
          .rounded-2xl { border-radius: 1rem !important; }
          .p-3 { padding: 0.75rem !important; }
          .p-4 { padding: 1rem !important; }
          .p-5 { padding: 1.25rem !important; }
          .py-1 { padding-top: 0.25rem !important; padding-bottom: 0.25rem !important; }
          .py-2 { padding-top: 0.5rem !important; padding-bottom: 0.5rem !important; }
          .py-3 { padding-top: 0.75rem !important; padding-bottom: 0.75rem !important; }
          .px-4 { padding-left: 1rem !important; padding-right: 1rem !important; }
          .mb-2 { margin-bottom: 0.5rem !important; }
          .mb-3 { margin-bottom: 0.75rem !important; }
          .mb-4 { margin-bottom: 1rem !important; }
          .mb-5 { margin-bottom: 1.25rem !important; }
          .mt-2 { margin-top: 0.5rem !important; }
          .mt-3 { margin-top: 0.75rem !important; }
          .mt-4 { margin-top: 1rem !important; }
          .gap-2 { gap: 0.5rem !important; }
          .gap-3 { gap: 0.75rem !important; }
          .gap-4 { gap: 1rem !important; }
          .flex { display: flex !important; }
          .items-center { align-items: center !important; }
          .justify-between { justify-content: space-between !important; }
          .text-right { text-align: right !important; }
          .text-center { text-align: center !important; }
          .border-t { border-top: 1px solid #e2e8f0 !important; }
          .border-slate-200 { border-color: #e2e8f0 !important; }
          .pt-3 { padding-top: 0.75rem !important; }
          .pt-4 { padding-top: 1rem !important; }
          table { width: 100%; border-collapse: collapse; }
          td { padding: 0.25rem 0; }
          img { max-width: 80px; height: auto; border-radius: 0.5rem; }
          /* Hide buttons in print */
          button, .btn, [data-testid*="btn"] { display: none !important; }
          /* Logo */
          .print-logo { max-width: 100px; height: auto; }
          @media print {
            body { padding: 0; }
            .print-container { padding: 10px; }
          }
        </style>
      </head>
      <body>
        <div class="print-container">
          ${printContent.innerHTML}
        </div>
        <script>
          window.onload = function() {
            setTimeout(function() {
              window.print();
              window.onafterprint = function() { window.close(); }
            }, 500);
          }
        </script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  // Load a quote from history
  const handleLoadQuote = (quote) => {
    // Customer info
    setCustomerData({
      name: quote.customer_name || '',
      phone: quote.customer_phone || '',
      email: quote.customer_email || ''
    });
    
    // Restore currency if saved
    if (quote.currency) {
      setCurrency(quote.currency);
    }
    
    // Terms
    setTermsAccepted(quote.terms_accepted || false);
    
    // Audiobook form data
    setFormData({
      bookTitle: quote.book_title || '',
      authorName: quote.author_name || '',
      wordCount: quote.word_count || 0,
      pageCount: quote.page_count || 0,
      coverUrl: quote.cover_url || '',
      coverFile: null,
      manualDuration: quote.manual_duration || 0
    });
    
    // Audiobook selections - handle null case for "Sin Audiolibro"
    setSelectedPackage(quote.package_id || null);
    setVoiceCloning(quote.voice_cloning || false);
    setManualDiscount(quote.manual_discount_percent || 0);
    setCustomDiscount(quote.custom_discount || 0);
    setCustomDiscountType(quote.custom_discount_type || 'percent');
    
    // Audiobook addons
    setSelectedDistribution(quote.distribution_id || 'dist_none');
    setSelectedBooktrailer(quote.booktrailer_id || 'bt_none');
    setSelectedLegalKit(quote.legal_kit_id || 'legal_none');
    setSelectedCoverDesign(quote.cover_design_id || 'cover_none');
    setSelectedBrandingPack(quote.branding_pack_id || 'branding_none');
    setSelectedMusicStock(quote.music_stock_id || 'music_none');
    setMusicStockQuantity(quote.music_stock_quantity || 1);
    setSelectedConsultoria(quote.consultoria_id || 'consultoria_none');
    setConsultoriaQuantity(quote.consultoria_quantity || 1);
    
    // Podcast
    setSelectedPodcastPackage(quote.podcast_package_id || null);
    setSelectedPodcastAddons(quote.podcast_addons || []);
    setPodcastQuantity(quote.podcast_quantity || 1);
    setPodcastData({
      title: quote.podcast_title || '',
      episodeDuration: quote.podcast_episode_duration || 0
    });
    
    // Open sections based on data
    if (quote.word_count > 0 || quote.page_count > 0 || quote.manual_duration > 0) {
      setAudiobookOpen(true);
    }
    if (quote.podcast_package_id) {
      setPodcastOpen(true);
    }

    // Project name
    if (quote.project_name) setProjectName(quote.project_name);

    // Walking Podcast
    if (quote.walking_podcast_package) {
      setSelectedWalkingPodcastPackage(quote.walking_podcast_package);
      setWalkingPodcastQuantity(quote.walking_podcast_quantity || 1);
      setWalkingSonidoDirectoDays(quote.walking_sonido_directo_days || 0);
      setWalkingAsistenteDays(quote.walking_asistente_days || 0);
      setWalkingPodcastOpen(true);
    }

    // Podcast post-production
    if (quote.podcast_post_production) setPodcastPostProduction(true);

    // Meditaciones
    if (quote.meditaciones_package_id) {
      setSelectedMeditacionesPackage(quote.meditaciones_package_id);
      setMeditacionesQuantity(quote.meditaciones_quantity || 1);
      setMeditacionesOpen(true);
    }

    // AudioCursos
    if (quote.audiocursos_package_id) {
      setSelectedAudiocursosPackage(quote.audiocursos_package_id);
      setAudiocursosQuantity(quote.audiocursos_quantity || 1);
      setAudiocursosOpen(true);
    }

    // New sections - restore data AND activate sections that are hidden by default
    const sectionsToActivate = [];

    if (quote.renta_equipo_data && Object.keys(quote.renta_equipo_data).length > 0) {
      setRentaEquipoData(quote.renta_equipo_data);
      setRentaEquipoOpen(true);
      sectionsToActivate.push('renta_equipo');
    }
    if (quote.transmision_data && Object.keys(quote.transmision_data).length > 0) {
      setTransmisionData(quote.transmision_data);
      setTransmisionOpen(true);
      sectionsToActivate.push('transmision');
    }
    if (quote.sonido_directo_data && Object.keys(quote.sonido_directo_data).length > 0) {
      setSonidoDirectoData(quote.sonido_directo_data);
      setSonidoDirectoOpen(true);
      sectionsToActivate.push('sonido_directo');
    }
    if (quote.foro_data && Object.keys(quote.foro_data).length > 0) {
      setForoData(quote.foro_data);
      setForoOpen(true);
      sectionsToActivate.push('foro');
    }
    if (quote.interpretacion_data && Object.keys(quote.interpretacion_data).length > 0) {
      setInterpretacionData(quote.interpretacion_data);
      setInterpretacionOpen(true);
      sectionsToActivate.push('interpretacion');
    }
    if (quote.sonorizacion_data && Object.keys(quote.sonorizacion_data).length > 0) {
      setSonorizacionData(quote.sonorizacion_data);
      setSonorizacionOpen(true);
      sectionsToActivate.push('sonorizacion');
    }

    // Editorial
    if (quote.editorial_data) {
      const ed = quote.editorial_data;
      if (ed.maquetacion) setSelectedMaquetacion(ed.maquetacion);
      if (ed.correccion) setSelectedCorreccion(ed.correccion);
      if (ed.correccionCuartillas) setCorreccionCuartillas(ed.correccionCuartillas);
      if (ed.portadaFisica) setSelectedPortadaFisica(ed.portadaFisica);
      if (ed.impresion) setSelectedImpresion(ed.impresion);
      if (ed.impresionCantidad) setImpresionCantidad(ed.impresionCantidad);
      if (ed.ghostWriting) setSelectedGhostWriting(ed.ghostWriting);
      setEditorialOpen(true);
      sectionsToActivate.push('editorial');
    }

    // Pautaje Digital
    if (quote.pautaje_digital_id) setSelectedPautajeDigital(quote.pautaje_digital_id);
    if (quote.commission_free_id) setSelectedCommissionFree(quote.commission_free_id);

    // Additional services addons
    if (quote.qr_dinamico_id) setSelectedQrDinamico(quote.qr_dinamico_id);
    if (quote.enlace_go_id) setSelectedEnlaceGo(quote.enlace_go_id);
    if (quote.nfc_vcard_id) setSelectedNfcVcard(quote.nfc_vcard_id);
    if (quote.separadores_id) setSelectedSeparadores(quote.separadores_id);
    if (quote.preventa_id) setSelectedPreventa(quote.preventa_id);
    if (quote.banner_promotion_id) {
      setSelectedBannerPromotion(quote.banner_promotion_id);
      setBannerPromotionMonths(quote.banner_promotion_months || 1);
    }
    if (quote.diseno_web_banner_id) setSelectedDisenoWebBanner(quote.diseno_web_banner_id);
    if (quote.photoshoot_id) setSelectedPhotoshoot(quote.photoshoot_id);
    if (quote.pagina_autor_id) setSelectedPaginaAutor(quote.pagina_autor_id);

    // Audio services
    if (quote.revision_audio) setRevisionAudio(quote.revision_audio);
    if (quote.masterizacion) setMasterizacion(quote.masterizacion);
    if (quote.voice_cloning) setVoiceCloning(quote.voice_cloning);
    if (quote.talento_premium) setTalentoPremium(quote.talento_premium);
    if (quote.extra_voices_ultra) setExtraVoicesUltra(quote.extra_voices_ultra);

    // ViubuxConnect
    if (quote.viubux_connect_active) setViubuxConnectActive(true);
    if (quote.vc_catalogo_active) setVcCatalogoActive(true);

    // Coproducción
    if (quote.coproduccion_active) setCoproduccionUserEnabled(true);

    // Imprenta addons
    if (quote.tarjetas_id) setSelectedTarjetas(quote.tarjetas_id);
    if (quote.separadores_impresos_id) setSelectedSeparadoresImpresos(quote.separadores_impresos_id);
    if (quote.envio_id) setSelectedEnvio(quote.envio_id);

    // Project cover
    if (quote.project_cover_url) setProjectCoverUrl(quote.project_cover_url);
    if (quote.voice_services) {
      setVoiceServicesQuantities(quote.voice_services);
      setVoiceServicesOpen(true);
      sectionsToActivate.push('voice_services');
    }
    if (quote.studio_hours) {
      setStudioHours(quote.studio_hours);
      setStudioFeesOpen(true);
      sectionsToActivate.push('studio_fees');
    }
    if (quote.audio_post_hours) {
      setAudioPostHours(quote.audio_post_hours);
      setStudioFeesOpen(true);
      sectionsToActivate.push('studio_fees');
    }
    if (quote.hand_free_rows) {
      setHandFreeRows(quote.hand_free_rows);
      setHandFreeOpen(true);
      sectionsToActivate.push('hand_free');
    }
    // Restore referral
    if (quote.referrer_code) {
      setReferrerCode(quote.referrer_code);
      setReferrerName(quote.referrer_name || '');
      setReferrerUserId(quote.referrer_user_id || '');
    }

    // Activate hidden sections so they become visible
    if (sectionsToActivate.length > 0) {
      setActivatedSections(prev => {
        const next = new Set(prev);
        sectionsToActivate.forEach(s => next.add(s));
        return next;
      });
    }
    // Also activate sections for podcast/walking/meditaciones/audiocursos if they have data
    if (quote.podcast_package_id) {
      setActivatedSections(prev => new Set([...prev, 'podcast']));
    }
    if (quote.walking_podcast_package) {
      setActivatedSections(prev => new Set([...prev, 'walking_podcast']));
    }
    if (quote.meditaciones_package_id) {
      setActivatedSections(prev => new Set([...prev, 'meditaciones']));
    }
    if (quote.audiocursos_package_id) {
      setActivatedSections(prev => new Set([...prev, 'audiocursos']));
    }
    
    toast.success('Cotización cargada', {
      description: `Se han cargado los datos de "${quote.book_title || 'la cotización'}"`
    });
  };

  const handleReset = () => {
    setProjectName('');
    setCustomerData({ name: '', phone: '', email: '' });
    setTermsAccepted(false);
    setFormData({
      bookTitle: '',
      authorName: '',
      wordCount: 0,
      pageCount: 0,
      coverUrl: '',
      coverFile: null,
      manualDuration: 0
    });
    setSelectedPackage('essential');
    setVoiceCloning(false);
    setExtraVoicesUltra(0);
    setRevisionAudio('rev_none');
    setMasterizacion('master_none');
    setMultivozActive(false);
    setMultivozVoces(1);
    setManualDiscount(0);
    setCustomDiscount(0);
    setCustomDiscountType('percent');
    setSelectedDistribution('dist_none');
    setSelectedBooktrailer('bt_none');
    setSelectedLegalKit('legal_none');
    setSelectedCoverDesign('cover_none');
    setSelectedBrandingPack('branding_none');
    setSelectedMusicStock('music_none');
    setMusicStockQuantity(1);
    setSelectedConsultoria('consultoria_none');
    setConsultoriaQuantity(1);
    // Reset new services
    setSelectedQrDinamico('qr_none');
    setSelectedEnlaceGo('enlace_none');
    setSelectedNfcVcard('nfc_none');
    setSelectedSeparadores('sep_none');
    setSelectedPreventa('preventa_none');
    setSelectedBannerPromotion('banner_none');
    setBannerPromotionMonths(1);
    setSelectedDisenoWebBanner('web_banner_none');
    setSelectedPhotoshoot('photoshoot_none');
    setSelectedPaginaAutor('pagina_autor_none');
    setSelectedPautajeDigital('pautaje_none');
    setPvp(0);
    setSalesGoal(0);
    setSavedQuoteCode('');
    setViubuxConnectActive(false);
    setVcCatalogoActive(false);
    setSelectedPodcastPackage(null);
    setSelectedPodcastAddons([]);
    setPodcastQuantity(1);
    setPodcastData({ title: '', episodeDuration: 0 });
    // Reset Meditaciones
    setSelectedMeditacionesPackage(null);
    setSelectedMeditacionesAddons([]);
    setMeditacionesQuantity(1);
    setMeditacionesData({ title: '', episodeDuration: 0 });
    // Reset AudioCursos
    setSelectedAudiocursosPackage(null);
    setSelectedAudiocursosAddons([]);
    setAudiocursosQuantity(1);
    setAudiocursosData({ title: '', episodeDuration: 0 });
    // Reset Walking Podcast
    setSelectedWalkingPodcastPackage(null);
    setWalkingPodcastQuantity(1);
    setWalkingPodcastData({ title: '', episodeDuration: 0 });
    setWalkingSonidoDirectoDays(0);
    setWalkingAsistenteDays(0);
    // Reset podcast post-production
    setPodcastPostProduction(false);
    // Reset new sections
    setRentaEquipoData({});
    setTransmisionData({});
    setSonidoDirectoData({});
    setForoData({});
    setInterpretacionData({});
    setSonorizacionData({});
  };

  const handleOpenEmailModal = () => {
    // Check if at least one service is selected (audiobook, podcast, meditaciones, audiocursos, or any addon)
    const hasAudiobook = selectedPackage && (formData.bookTitle || formData.wordCount > 0 || formData.pageCount > 0 || formData.manualDuration > 0);
    const hasPodcast = selectedPodcastPackage !== null;
    const hasMeditaciones = selectedMeditacionesPackage !== null;
    const hasAudiocursos = selectedAudiocursosPackage !== null;
    const hasAddons = audiobookAddonsTotal > 0;
    const hasAnyNewSection = (selectedWalkingPodcastPackage !== null) || editorialSubtotal > 0 || rentaEquipoTotal > 0 || transmisionTotal > 0 || sonidoDirectoTotal > 0 || foroTotal > 0 || interpretacionTotal > 0 || sonorizacionTotal > 0 || voiceServicesTotal > 0 || studioFeesTotal > 0 || handFreeTotal > 0;
    
    if (!hasAudiobook && !hasPodcast && !hasMeditaciones && !hasAudiocursos && !hasAddons && !hasAnyNewSection) {
      toast.error('Selecciona al menos un servicio antes de enviar');
      return;
    }
    setEmailModalOpen(true);
  };

  // Recover quote by code
  const handleRecoverQuote = async () => {
    if (!recoveryCode.trim()) {
      toast.error('Ingresa un código de recuperación');
      return;
    }
    
    setIsRecovering(true);
    try {
      const response = await axios.get(`${API}/quotes/recover/${recoveryCode.trim()}`);
      const quote = response.data;
      
      // Load the quote data using the existing handleLoadQuote function
      handleLoadQuote(quote);
      setSavedQuoteCode(quote.recovery_code || recoveryCode.trim());
      
      toast.success('Cotización recuperada correctamente');
      setRecoveryCode('');
      setShowRecoveryInput(false);
    } catch (error) {
      console.error('Error recovering quote:', error);
      if (error.response?.status === 404) {
        toast.error('Código de recuperación no válido');
      } else {
        toast.error('Error al recuperar la cotización');
      }
    } finally {
      setIsRecovering(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#080c2d] flex items-center justify-center">
        <div className="text-center">
          <img src={VIUBUX_ISO} alt="Viubux" className="w-16 h-16 mx-auto mb-4 animate-pulse" />
          <p className="text-slate-400">Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#080c2d] bg-pattern" data-testid="dashboard">
      {/* Header */}
      <HeaderBar
        savedQuoteCode={savedQuoteCode}
        showRecoveryInput={showRecoveryInput}
        setShowRecoveryInput={setShowRecoveryInput}
        recoveryCode={recoveryCode}
        setRecoveryCode={setRecoveryCode}
        handleRecoverQuote={handleRecoverQuote}
        isRecovering={isRecovering}
        currency={currency}
        toggleCurrency={toggleCurrency}
        onShowVideoModal={() => setShowVideoModal(true)}
        settings={settings}
        onSaveSettings={handleSaveSettings}
        settingsOpen={settingsOpen}
        setSettingsOpen={setSettingsOpen}
        showDiscounts={showDiscounts}
        setShowDiscounts={setShowDiscounts}
        customDiscount={customDiscount}
        setCustomDiscount={setCustomDiscount}
        customDiscountType={customDiscountType}
        setCustomDiscountType={setCustomDiscountType}
        onLoadQuote={handleLoadQuote}
        referrerCode={referrerCode}
        referrerName={referrerName}
        showReferralInput={showReferralInput}
        setShowReferralInput={setShowReferralInput}
        referralInputValue={referralInputValue}
        setReferralInputValue={setReferralInputValue}
        verifyReferral={verifyReferral}
        clearReferral={clearReferral}
        isVerifyingReferral={isVerifyingReferral}
      />

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Project Name */}
        <div className="mb-6">
          <div className="relative">
            <input
              type="text"
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              placeholder="Nombre del Proyecto"
              className="w-full bg-transparent border-0 border-b-2 border-white/20 focus:border-[#aa2aff] text-white text-2xl font-bold placeholder:text-slate-600 placeholder:font-normal py-3 px-1 outline-none transition-colors"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
              data-testid="input-project-name"
            />
          </div>
          {/* Project Cover Upload */}
          <div className="mt-3 flex items-center gap-3">
            {projectCoverUrl && (
              <img src={projectCoverUrl} alt="Portada" className="w-16 h-16 rounded-lg object-cover border border-white/20" />
            )}
            <label className="cursor-pointer flex items-center gap-2 text-xs text-slate-400 hover:text-[#aa2aff] transition-colors" data-testid="label-project-cover">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
              {projectCoverUrl ? 'Cambiar portada' : 'Agregar portada del proyecto'}
              <input
                type="file"
                accept="image/*"
                className="hidden"
                data-testid="input-project-cover"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    const reader = new FileReader();
                    reader.onload = (ev) => setProjectCoverUrl(ev.target.result);
                    reader.readAsDataURL(file);
                  }
                }}
              />
            </label>
            {projectCoverUrl && (
              <button
                onClick={() => setProjectCoverUrl('')}
                className="text-xs text-red-400 hover:text-red-300"
                data-testid="btn-remove-project-cover"
              >
                Quitar
              </button>
            )}
          </div>
        </div>

        {/* Customer Form */}
        <CustomerForm 
          customerData={customerData}
          onChange={setCustomerData}
          invoiceRequested={invoiceRequested}
          onInvoiceRequestedChange={setInvoiceRequested}
          invoiceFiscalData={invoiceFiscalData}
          onInvoiceFiscalDataChange={setInvoiceFiscalData}
        />
        
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Left Column - Services */}
          <div className="lg:w-[56%] space-y-4">
            {/* ViubuxConnect Section - First */}
            {(settings.show_viubux_connect_section !== false || activatedSections.has('viubux_connect')) && (
              <ViubuxConnectSection
                isOpen={viubuxConnectOpen}
                onToggle={() => setViubuxConnectOpen(!viubuxConnectOpen)}
                isActive={viubuxConnectActive}
                onActiveChange={setViubuxConnectActive}
                catalogoActive={vcCatalogoActive}
                onCatalogoChange={setVcCatalogoActive}
                catalogoPrice={settings.price_vc_catalogo || 2248}
                showDiscountPerItem={showDiscountPerItem}
                onShowDiscountPerItemChange={setShowDiscountPerItem}
                formatPrice={formatPrice}
                currency={currency}
              />
            )}

            {/* Audiobook Section */}
            {settings.show_audiobook_section !== false && (
              <>
                <AudiobookSection
                  isOpen={audiobookOpen}
                  onToggle={() => setAudiobookOpen(!audiobookOpen)}
                  formData={formData}
                  onFormChange={handleFormChange}
                  selectedPackage={selectedPackage}
                  onPackageSelect={setSelectedPackage}
                  packages={settings.show_ultra_package === false ? packages.filter(p => p.id !== 'ultra') : packages}
                  formatPrice={formatPrice}
                  currency={currency}
                  exchangeRate={settings.usd_exchange_rate || 17.5}
                  manualDiscount={manualDiscount}
                  volumeDiscount={audiobookQuote?.volumeDiscountPercent || 0}
                  manualDiscounts={manualDiscounts}
                  durationHours={audiobookQuote?.estimatedDurationHours || 0}
                  onManualDiscountChange={setManualDiscount}
                  showDiscounts={showDiscounts}
                  quote={audiobookQuote}
                  pvp={pvp}
                  onPvpChange={setPvp}
                  audiobookSubtotal={(audiobookQuote?.grandTotal || 0) + audiobookAddonsTotal}
                  salesGoal={salesGoal}
                  onSalesGoalChange={setSalesGoal}
                  onShowPackageDetails={() => setShowPackageDetails(true)}
                  voiceCloning={voiceCloning}
                  onVoiceCloningChange={setVoiceCloning}
                  talentoPremium={talentoPremium}
                  onTalentoPremiumChange={setTalentoPremium}
                  talentoPremiumPricePerHour={settings.price_talento_premium || 1500}
                  extraVoicesUltra={extraVoicesUltra}
                  onExtraVoicesUltraChange={setExtraVoicesUltra}
                  revisionAudio={revisionAudio}
                  onRevisionAudioChange={setRevisionAudio}
                  masterizacion={masterizacion}
                  onMasterizacionChange={setMasterizacion}
                  multivozActive={multivozActive}
                  onMultivozActiveChange={setMultivozActive}
                  multivozVoces={multivozVoces}
                  onMultivozVocesChange={setMultivozVoces}
                />
                <PackageDetailsModal
                  isOpen={showPackageDetails}
                  onClose={() => setShowPackageDetails(false)}
                  onSelectPackage={setSelectedPackage}
                />
              </>
            )}

            {/* Podcast Section */}
            {(settings.show_podcast_section !== false || activatedSections.has('podcast')) && (
              <PodcastSection
                isOpen={podcastOpen}
                onToggle={() => setPodcastOpen(!podcastOpen)}
                selectedPackage={selectedPodcastPackage}
                onPackageSelect={setSelectedPodcastPackage}
                selectedAddons={selectedPodcastAddons}
                onAddonToggle={handlePodcastAddonToggle}
                podcastQuantity={podcastQuantity}
                onQuantityChange={setPodcastQuantity}
                podcastData={podcastData}
                onPodcastDataChange={setPodcastData}
                postProduction={podcastPostProduction}
                onPostProductionChange={setPodcastPostProduction}
                formatPrice={formatPrice}
                currency={currency}
                servicePrices={settings}
              />
            )}

            {/* Walking Podcast Section */}
            {(settings.show_walking_podcast_section !== false || activatedSections.has('walking_podcast')) && (
              <WalkingPodcastSection
                isOpen={walkingPodcastOpen}
                onToggle={() => setWalkingPodcastOpen(!walkingPodcastOpen)}
                selectedPackage={selectedWalkingPodcastPackage}
                onPackageSelect={setSelectedWalkingPodcastPackage}
                walkingPodcastQuantity={walkingPodcastQuantity}
                onQuantityChange={setWalkingPodcastQuantity}
                walkingPodcastData={walkingPodcastData}
                onDataChange={setWalkingPodcastData}
                sonidoDirectoDays={walkingSonidoDirectoDays}
                onSonidoDirectoDaysChange={setWalkingSonidoDirectoDays}
                asistenteDays={walkingAsistenteDays}
                onAsistenteDaysChange={setWalkingAsistenteDays}
                formatPrice={formatPrice}
                currency={currency}
                servicePrices={settings}
              />
            )}

            {/* Meditaciones Guiadas Section */}
            {(settings.show_meditaciones_section !== false || activatedSections.has('meditaciones')) && (
            <MeditacionesSection
              isOpen={meditacionesOpen}
              onToggle={() => setMeditacionesOpen(!meditacionesOpen)}
              selectedPackage={selectedMeditacionesPackage}
              onPackageSelect={setSelectedMeditacionesPackage}
              selectedAddons={selectedMeditacionesAddons}
              onAddonToggle={(addonId) => {
                setSelectedMeditacionesAddons(prev => 
                  prev.includes(addonId) 
                    ? prev.filter(id => id !== addonId)
                    : [...prev, addonId]
                );
              }}
              meditacionesQuantity={meditacionesQuantity}
              onQuantityChange={setMeditacionesQuantity}
              meditacionesData={meditacionesData}
              onMeditacionesDataChange={setMeditacionesData}
              formatPrice={formatPrice}
              currency={currency}
              servicePrices={settings}
            />
            )}

            {/* AudioCursos Section */}
            {(settings.show_audiocursos_section !== false || activatedSections.has('audiocursos')) && (
            <AudioCursosSection
              isOpen={audiocursosOpen}
              onToggle={() => setAudiocursosOpen(!audiocursosOpen)}
              selectedPackage={selectedAudiocursosPackage}
              onPackageSelect={setSelectedAudiocursosPackage}
              selectedAddons={selectedAudiocursosAddons}
              onAddonToggle={(addonId) => {
                setSelectedAudiocursosAddons(prev => 
                  prev.includes(addonId) 
                    ? prev.filter(id => id !== addonId)
                    : [...prev, addonId]
                );
              }}
              audiocursosQuantity={audiocursosQuantity}
              onQuantityChange={setAudiocursosQuantity}
              audiocursosData={audiocursosData}
              onAudiocursosDataChange={setAudiocursosData}
              formatPrice={formatPrice}
              currency={currency}
              servicePrices={settings}
            />
            )}

            {/* Servicios Adicionales Section - Independent */}
            {(settings.show_servicios_section !== false || activatedSections.has('servicios_adicionales') || activatedSections.has('editorial')) && (
              <ServiciosAdicionalesSection
                isOpen={serviciosOpen}
                onToggle={() => setServiciosOpen(!serviciosOpen)}
                selectedDistribution={selectedDistribution}
                selectedBooktrailer={selectedBooktrailer}
                selectedLegalKit={selectedLegalKit}
                selectedCoverDesign={selectedCoverDesign}
                selectedBrandingPack={selectedBrandingPack}
                selectedMusicStock={selectedMusicStock}
                musicStockQuantity={musicStockQuantity}
                selectedConsultoria={selectedConsultoria}
                consultoriaQuantity={consultoriaQuantity}
                selectedQrDinamico={selectedQrDinamico}
                selectedEnlaceGo={selectedEnlaceGo}
                selectedNfcVcard={selectedNfcVcard}
                selectedSeparadores={selectedSeparadores}
                selectedPreventa={selectedPreventa}
                selectedBannerPromotion={selectedBannerPromotion}
                bannerPromotionMonths={bannerPromotionMonths}
                selectedDisenoWebBanner={selectedDisenoWebBanner}
                selectedPhotoshoot={selectedPhotoshoot}
                selectedPaginaAutor={selectedPaginaAutor}
                selectedPautajeDigital={selectedPautajeDigital}
                selectedCommissionFree={selectedCommissionFree}
                onDistributionChange={setSelectedDistribution}
                onBooktrailerChange={setSelectedBooktrailer}
                onLegalKitChange={setSelectedLegalKit}
                onCoverDesignChange={setSelectedCoverDesign}
                onBrandingPackChange={setSelectedBrandingPack}
                onMusicStockChange={setSelectedMusicStock}
                onMusicStockQuantityChange={setMusicStockQuantity}
                onConsultoriaChange={setSelectedConsultoria}
                onConsultoriaQuantityChange={setConsultoriaQuantity}
                onQrDinamicoChange={setSelectedQrDinamico}
                onEnlaceGoChange={setSelectedEnlaceGo}
                onNfcVcardChange={setSelectedNfcVcard}
                onSeparadoresChange={setSelectedSeparadores}
                onPreventaChange={setSelectedPreventa}
                onBannerPromotionChange={setSelectedBannerPromotion}
                onBannerPromotionMonthsChange={setBannerPromotionMonths}
                onDisenoWebBannerChange={setSelectedDisenoWebBanner}
                onPhotoshootChange={setSelectedPhotoshoot}
                onPaginaAutorChange={setSelectedPaginaAutor}
                onPautajeDigitalChange={setSelectedPautajeDigital}
                onCommissionFreeChange={setSelectedCommissionFree}
                onTarjetasChange={setSelectedTarjetas}
                onSeparadoresImpresosChange={setSelectedSeparadoresImpresos}
                onEnvioChange={setSelectedEnvio}
                revisionAudio={revisionAudio}
                onRevisionAudioChange={setRevisionAudio}
                masterizacion={masterizacion}
                onMasterizacionChange={setMasterizacion}
                selectedTarjetas={selectedTarjetas}
                selectedSeparadoresImpresos={selectedSeparadoresImpresos}
                selectedEnvio={selectedEnvio}
                formatPrice={formatPrice}
                currency={currency}
                addonsTotal={audiobookAddonsTotal}
                servicePrices={settings}
                editorialSection={(settings.show_editorial_section !== false || activatedSections.has('editorial')) ?
                  <EditorialSection
                    isOpen={editorialOpen}
                    onToggle={() => setEditorialOpen(!editorialOpen)}
                    selectedMaquetacion={selectedMaquetacion}
                    onMaquetacionChange={setSelectedMaquetacion}
                    selectedCorreccion={selectedCorreccion}
                    onCorreccionChange={setSelectedCorreccion}
                    correccionCuartillas={correccionCuartillas}
                    onCorreccionCuartillasChange={setCorreccionCuartillas}
                    selectedPortadaFisica={selectedPortadaFisica}
                    onPortadaFisicaChange={setSelectedPortadaFisica}
                    selectedImpresion={selectedImpresion}
                    onImpresionChange={setSelectedImpresion}
                    impresionCantidad={impresionCantidad}
                    onImpresionCantidadChange={setImpresionCantidad}
                    selectedGhostWriting={selectedGhostWriting}
                    onGhostWritingChange={setSelectedGhostWriting}
                    formatPrice={formatPrice}
                    currency={currency}
                    settings={settings}
                  />
                  : null
                }
              />
            )}

            {/* Servicios de Voz */}
            {(settings.show_voice_services_section !== false || activatedSections.has('voice_services')) && (
              <VoiceServicesSection
                isOpen={voiceServicesOpen}
                onToggle={() => setVoiceServicesOpen(!voiceServicesOpen)}
                quantities={voiceServicesQuantities}
                onQuantityChange={(id, qty) => setVoiceServicesQuantities(prev => ({ ...prev, [id]: qty }))}
                formatPrice={formatPrice}
                currency={currency}
              />
            )}

            {/* Studio Fees */}
            {(settings.show_studio_fees_section !== false || activatedSections.has('studio_fees')) && (
              <StudioFeesSection
                isOpen={studioFeesOpen}
                onToggle={() => setStudioFeesOpen(!studioFeesOpen)}
                studioHours={studioHours}
                onStudioHoursChange={setStudioHours}
                audioPostHours={audioPostHours}
                onAudioPostHoursChange={setAudioPostHours}
                formatPrice={formatPrice}
                currency={currency}
                settings={settings}
              />
            )}

            {/* Hand-Free */}
            {(settings.show_hand_free_section !== false || activatedSections.has('hand_free')) && (
              <HandFreeSection
                isOpen={handFreeOpen}
                onToggle={() => setHandFreeOpen(!handFreeOpen)}
                rows={handFreeRows}
                onRowsChange={setHandFreeRows}
                formatPrice={formatPrice}
                currency={currency}
              />
            )}

            {/* Renta de Equipo */}
            {(settings.show_renta_equipo_section !== false || activatedSections.has('renta_equipo')) && (
              <RentaEquipoSection isOpen={rentaEquipoOpen} onToggle={() => setRentaEquipoOpen(!rentaEquipoOpen)}
                data={rentaEquipoData} onChange={setRentaEquipoData} formatPrice={formatPrice} currency={currency} settings={settings} />
            )}

            {/* Transmisión en Vivo */}
            {settings.show_transmision_section !== false && (
              <TransmisionEnVivoSection isOpen={transmisionOpen} onToggle={() => setTransmisionOpen(!transmisionOpen)}
                data={transmisionData} onChange={setTransmisionData} formatPrice={formatPrice} currency={currency} settings={settings} />
            )}

            {/* Sonido Directo */}
            {(settings.show_sonido_directo_section !== false || activatedSections.has('sonido_directo')) && (
              <SonidoDirectoSection isOpen={sonidoDirectoOpen} onToggle={() => setSonidoDirectoOpen(!sonidoDirectoOpen)}
                data={sonidoDirectoData} onChange={setSonidoDirectoData} formatPrice={formatPrice} currency={currency} settings={settings} />
            )}

            {/* Foro */}
            {settings.show_foro_section !== false && (
              <ForoSection isOpen={foroOpen} onToggle={() => setForoOpen(!foroOpen)}
                data={foroData} onChange={setForoData} formatPrice={formatPrice} currency={currency} settings={settings} />
            )}

            {/* Interpretación Simultánea */}
            {(settings.show_interpretacion_section !== false || activatedSections.has('interpretacion')) && (
              <InterpretacionSection isOpen={interpretacionOpen} onToggle={() => setInterpretacionOpen(!interpretacionOpen)}
                data={interpretacionData} onChange={setInterpretacionData} formatPrice={formatPrice} currency={currency} settings={settings} />
            )}

            {/* Sonorización para Eventos */}
            {(settings.show_sonorizacion_section !== false || activatedSections.has('sonorizacion')) && (
              <SonorizacionSection isOpen={sonorizacionOpen} onToggle={() => setSonorizacionOpen(!sonorizacionOpen)}
                data={sonorizacionData} onChange={setSonorizacionData} formatPrice={formatPrice} currency={currency} settings={settings} />
            )}

            {/* Co-Inversión Checkbox - only shown when admin enables co-producción */}
            {coproduccionAvailable && (
              <div className="bg-gradient-to-r from-amber-500/10 to-amber-600/10 border border-amber-500/30 rounded-xl p-4">
                <label className="flex items-center gap-3 cursor-pointer" data-testid="coinversion-checkbox-label">
                  <input
                    type="checkbox"
                    checked={coproduccionUserEnabled}
                    onChange={(e) => setCoproduccionUserEnabled(e.target.checked)}
                    className="w-5 h-5 rounded border-amber-500/50 bg-white/10 text-amber-500 focus:ring-amber-500 accent-amber-500"
                    data-testid="coinversion-checkbox"
                  />
                  <div>
                    <span className="text-white font-semibold text-sm" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                      Aplicar Co-Inversión ({coproduccionPercent}%)
                    </span>
                    <p className="text-slate-400 text-xs mt-0.5">
                      Un co-inversionista cubre el {coproduccionPercent}% del total. Tu inversión se reduce a {formatPrice(clientPayAmount)} {currency}.
                    </p>
                  </div>
                </label>
              </div>
            )}

            {/* Terms and Conditions */}
            <TermsCheckbox 
              checked={termsAccepted}
              onChange={setTermsAccepted}
              highlight={termsHighlight}
            />

            {/* Payment Info */}
            <PaymentInfo 
              grandTotal={finalTotalWithCustomDiscount}
              clientPayAmount={clientPayAmount}
              showCoproduccion={showCoproduccion}
              coproduccionPercent={coproduccionPercent}
              coproduccionAmount={coproduccionAmount}
              formatPrice={formatPrice}
              currency={currency}
              customerEmail={customerData.email}
              customerName={customerData.name}
              customerPhone={customerData.phone}
              onSaveQuote={handleSaveQuote}
              termsAccepted={termsAccepted}
              savedQuoteCode={savedQuoteCode}
              showBankTransfer={settings.show_bank_transfer !== false}
            />

            {/* Action Buttons */}
            <div className="space-y-3 pt-2">
              <div className="flex gap-3">
                <Button
                  onClick={handleReset}
                  variant="outline"
                  className="flex-1 border-slate-600 text-slate-300 hover:bg-white/5"
                  data-testid="btn-reset"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Limpiar
                </Button>
                <Button
                  onClick={handleSaveQuote}
                  className="flex-1 bg-[#aa2aff] hover:bg-[#7012b2] text-white shadow-lg glow-accent-hover"
                  data-testid="btn-save-quote"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Guardar
                </Button>
              </div>
              
              <Button
                onClick={handleOpenEmailModal}
                className="w-full bg-gradient-to-r from-[#003fdd] to-[#272b96] hover:from-[#272b96] hover:to-[#003fdd] text-white"
                data-testid="btn-open-email-modal"
              >
                <Mail className="w-4 h-4 mr-2" />
                Enviar Cotización por Email
              </Button>
            </div>
          </div>

          {/* Right Column - Preview (sticky on desktop, below on mobile) */}
          <div className="lg:w-[42%]">
            <div className="lg:sticky lg:top-4 lg:max-h-[calc(100vh-2rem)] lg:overflow-y-auto lg:overflow-x-hidden lg:pr-1" style={{ scrollbarWidth: 'thin', scrollbarColor: '#aa2aff33 transparent' }}>
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-lg font-bold text-white flex items-center gap-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  <ChevronRight className="w-4 h-4 text-[#aa2aff]" />
                  Vista Previa
                </h2>
                <Button
                  onClick={handlePrint}
                  variant="ghost"
                  size="sm"
                  className="text-slate-400 hover:text-white text-xs"
                  data-testid="btn-print"
                >
                  <Download className="w-3.5 h-3.5 mr-1" />
                  Imprimir
                </Button>
              </div>
              
              <div>
              
              <QuotePreview
                ref={quotePreviewRef}
                formData={formData}
                quote={audiobookQuote}
                formatPrice={formatPrice}
                formatDuration={formatDuration}
                currency={currency}
                distributionAddon={distributionAddon}
                booktrailerAddon={booktrailerAddon}
                legalKitAddon={legalKitAddon}
                coverDesignAddon={coverDesignAddon}
                brandingPackAddon={brandingPackAddon}
                musicStockAddon={musicStockAddon}
                consultoriaAddon={consultoriaAddon}
                qrDinamicoAddon={qrDinamicoAddon}
                enlaceGoAddon={enlaceGoAddon}
                nfcVcardAddon={nfcVcardAddon}
                separadoresAddon={separadoresAddon}
                preventaAddon={preventaAddon}
                bannerPromotionAddon={bannerPromotionAddon}
                disenoWebBannerAddon={disenoWebBannerAddon}
                photoshootAddon={photoshootAddon}
                paginaAutorAddon={paginaAutorAddon}
                pautajeDigitalAddon={pautajeDigitalAddon}
                commissionFreeAddon={commissionFreeAddon}
                tarjetasAddon={tarjetasAddon}
                separadoresImpresosAddon={separadoresImpresosAddon}
                envioAddon={envioAddon}
                addonsTotal={audiobookAddonsTotal}
                voiceCloningCost={voiceCloningCost}
                talentoPremiumCost={talentoPremiumCost}
                extraVoicesUltraCost={extraVoicesUltraCost}
                extraVoicesUltra={extraVoicesUltra}
                revisionAudioCost={revisionAudioCost}
                revisionAudio={revisionAudio}
                masterizacionCost={masterizacionCost}
                masterizacion={masterizacion}
                multivozCost={multivozCost}
                multivozActive={multivozActive}
                multivozVoces={multivozVoces}
                customDiscount={customDiscount}
                customDiscountType={customDiscountType}
                customDiscountAmount={customDiscountAmount}
                viubuxConnectActive={viubuxConnectActive}
                showDiscountPerItem={showDiscountPerItem}
                viubuxConnectDiscountAmount={viubuxConnectDiscountAmount}
                viubuxConnectInscription={viubuxConnectInscription}
                vcCatalogoCost={vcCatalogoCost}
                subtotalAfterDiscount={subtotalAfterDiscount}
                ivaAmount={ivaAmount}
                finalTotalWithCustomDiscount={finalTotalWithCustomDiscount}
                podcastPackage={selectedPodcastPkg}
                podcastAddons={selectedPodcastAddons.map(id => PODCAST_ADDONS.find(a => a.id === id)).filter(Boolean)}
                podcastTotal={podcastTotal}
                podcastQuantity={podcastQuantity}
                podcastRecordingHours={estimatedRecordingHours}
                hasValidAudiobookData={hasValidAudiobookData}
                voiceServicesQuantities={voiceServicesQuantities}
                voiceServicesTotal={voiceServicesTotal}
                studioHours={studioHours}
                audioPostHours={audioPostHours}
                studioFeesTotal={studioFeesTotal}
                handFreeRows={handFreeRows}
                handFreeTotal={handFreeTotal}
                settings={settings}
                customerName={customerData.name}
                customerEmail={customerData.email}
                customerPhone={customerData.phone}
                // Meditaciones props
                meditacionesPackage={selectedMeditacionesPackage ? {
                  id: selectedMeditacionesPackage,
                  name: selectedMeditacionesPackage === 'med_essential' ? 'Essential' : 
                        selectedMeditacionesPackage === 'med_platino' ? 'Platino' : 
                        selectedMeditacionesPackage === 'med_premium' ? 'Premium' : '',
                  price: getMeditacionesPackagePrice(selectedMeditacionesPackage)
                } : null}
                meditacionesTotal={meditacionesTotal}
                meditacionesQuantity={meditacionesQuantity}
                meditacionesRecordingHours={meditacionesEstimatedHours}
                // AudioCursos props
                audiocursosPackage={selectedAudiocursosPackage ? {
                  id: selectedAudiocursosPackage,
                  name: selectedAudiocursosPackage === 'ac_essential' ? 'Essential' : 
                        selectedAudiocursosPackage === 'ac_platino' ? 'Platino' : 
                        selectedAudiocursosPackage === 'ac_premium' ? 'Premium' : '',
                  price: getAudiocursosPackagePrice(selectedAudiocursosPackage)
                } : null}
                audiocursosTotal={audiocursosTotal}
                audiocursosQuantity={audiocursosQuantity}
                audiocursosRecordingHours={audiocursosEstimatedHours}
                savedQuoteCode={savedQuoteCode}
                projectName={projectName}
                projectCoverUrl={projectCoverUrl}
                walkingPodcastPackage={selectedWalkingPodcastPackage ? {
                  id: selectedWalkingPodcastPackage,
                  name: selectedWalkingPodcastPackage === 'wpod_essential' ? 'Essential' :
                        selectedWalkingPodcastPackage === 'wpod_platino' ? 'Platino' :
                        selectedWalkingPodcastPackage === 'wpod_premium' ? 'Premium' : '',
                  price: walkingPodcastPkg
                } : null}
                walkingPodcastTotal={walkingPodcastTotal}
                walkingPodcastQuantity={walkingPodcastQuantity}
                walkingPodcastRecordingHours={walkingEstimatedHours}
                walkingSonidoDirectoTotal={walkingSonidoTotal}
                walkingAsistenteTotal={walkingAsistenteTotal}
                // New sections
                rentaEquipoTotal={rentaEquipoTotal}
                rentaEquipoData={rentaEquipoData}
                transmisionTotal={transmisionTotal}
                transmisionData={transmisionData}
                sonidoDirectoTotal={sonidoDirectoTotal}
                sonidoDirectoData={sonidoDirectoData}
                foroTotal={foroTotal}
                foroData={foroData}
                interpretacionData={interpretacionData}
                interpretacionTotal={interpretacionTotal}
                sonorizacionData={sonorizacionData}
                sonorizacionTotal={sonorizacionTotal}
                podcastPostProduction={podcastPostProduction}
                showCoproduccion={showCoproduccion}
                coproduccionPercent={coproduccionPercent}
                coproduccionAmount={coproduccionAmount}
                clientPayAmount={clientPayAmount}
                exchangeRate={settings.usd_exchange_rate || 17.5}
                // Editorial Services props
                editorialData={{
                  maquetacion: selectedMaquetacion,
                  correccion: selectedCorreccion,
                  correccionCuartillas: correccionCuartillas,
                  portadaFisica: selectedPortadaFisica,
                  impresion: selectedImpresion,
                  impresionCantidad: impresionCantidad,
                  subtotal: editorialSubtotal,
                  maquetacionTotal: editorialMaquetacionTotal,
                  correccionTotal: editorialCorreccionTotal,
                  portadaFisicaTotal: editorialPortadaFisicaTotal,
                  impresionTotal: editorialImpresionTotal,
                  ghostWriting: selectedGhostWriting,
                  ghostWritingTotal: editorialGhostWritingTotal,
                }}
              />
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer with Contact Info */}
      <FooterSection />

      {/* Email Modal */}
      <SendEmailModal
        isOpen={emailModalOpen}
        onClose={() => setEmailModalOpen(false)}
        formData={formData}
        quote={audiobookQuote}
        formatPrice={formatPrice}
        formatDuration={formatDuration}
        distributionAddon={distributionAddon}
        booktrailerAddon={booktrailerAddon}
        legalKitAddon={legalKitAddon}
        coverDesignAddon={coverDesignAddon}
        brandingPackAddon={brandingPackAddon}
        musicStockAddon={musicStockAddon}
        consultoriaAddon={consultoriaAddon}
        qrDinamicoAddon={qrDinamicoAddon}
        enlaceGoAddon={enlaceGoAddon}
        nfcVcardAddon={nfcVcardAddon}
        separadoresAddon={separadoresAddon}
        preventaAddon={preventaAddon}
        bannerPromotionAddon={bannerPromotionAddon}
        disenoWebBannerAddon={disenoWebBannerAddon}
        photoshootAddon={photoshootAddon}
        paginaAutorAddon={paginaAutorAddon}
        pautajeDigitalAddon={pautajeDigitalAddon}
        commissionFreeAddon={commissionFreeAddon}
        addonsTotal={audiobookAddonsTotal}
        voiceCloningCost={voiceCloningCost}
        talentoPremiumCost={talentoPremiumCost}
        extraVoicesUltraCost={extraVoicesUltraCost}
        extraVoicesUltra={extraVoicesUltra}
        multivozCost={multivozCost}
        multivozVoces={multivozVoces}
        revisionAudioCost={revisionAudioCost}
        masterizacionCost={masterizacionCost}
        podcastPackage={selectedPodcastPkg}
        podcastAddons={selectedPodcastAddons.map(id => PODCAST_ADDONS.find(a => a.id === id)).filter(Boolean)}
        podcastTotal={podcastTotal}
        podcastQuantity={podcastQuantity}
        podcastData={podcastData}
        podcastPostProduction={podcastPostProduction}
        estimatedRecordingHours={estimatedRecordingHours}
        walkingPodcastPackage={selectedWalkingPodcastPackage ? {
          name: selectedWalkingPodcastPackage === 'wpod_essential' ? 'Essential' :
                selectedWalkingPodcastPackage === 'wpod_platino' ? 'Platino' :
                selectedWalkingPodcastPackage === 'wpod_premium' ? 'Premium' : '',
          price: walkingPodcastPkg
        } : null}
        walkingPodcastTotal={walkingPodcastTotal}
        walkingPodcastQuantity={walkingPodcastQuantity}
        walkingPodcastRecordingHours={walkingEstimatedHours}
        walkingSonidoDirectoTotal={walkingSonidoTotal}
        walkingAsistenteTotal={walkingAsistenteTotal}
        rentaEquipoData={rentaEquipoData}
        rentaEquipoTotal={rentaEquipoTotal}
        transmisionData={transmisionData}
        transmisionTotal={transmisionTotal}
        sonidoDirectoData={sonidoDirectoData}
        sonidoDirectoTotal={sonidoDirectoTotal}
        foroData={foroData}
        foroTotal={foroTotal}
        interpretacionData={interpretacionData}
        interpretacionTotal={interpretacionTotal}
        sonorizacionData={sonorizacionData}
        sonorizacionTotal={sonorizacionTotal}
        projectName={projectName}
        projectCoverUrl={projectCoverUrl}
        grandTotal={finalTotalWithCustomDiscount}
        subtotalBeforeDiscount={subtotalBeforeCustomDiscount}
        customDiscount={customDiscount}
        customDiscountType={customDiscountType}
        customDiscountAmount={customDiscountAmount}
        subtotalAfterDiscount={subtotalAfterDiscount}
        ivaAmount={ivaAmount}
        customerData={customerData}
        currency={currency}
        exchangeRate={settings.usd_exchange_rate || 17.5}
        savedQuoteCode={savedQuoteCode}
        showCoproduccion={showCoproduccion}
        coproduccionPercent={coproduccionPercent}
        coproduccionAmount={coproduccionAmount}
        clientPayAmount={clientPayAmount}
        viubuxConnectActive={viubuxConnectActive}
        showDiscountPerItem={showDiscountPerItem}
        viubuxConnectInscription={viubuxConnectInscription}
        vcCatalogoCost={vcCatalogoCost}
        viubuxConnectDiscountAmount={viubuxConnectDiscountAmount}
        editorialData={{
          maquetacion: selectedMaquetacion,
          correccion: selectedCorreccion,
          correccionCuartillas: correccionCuartillas,
          portadaFisica: selectedPortadaFisica,
          impresion: selectedImpresion,
          impresionCantidad: impresionCantidad,
          subtotal: editorialSubtotal,
          maquetacionTotal: editorialMaquetacionTotal,
          correccionTotal: editorialCorreccionTotal,
          portadaFisicaTotal: editorialPortadaFisicaTotal,
          impresionTotal: editorialImpresionTotal,
          ghostWriting: selectedGhostWriting,
          ghostWritingTotal: editorialGhostWritingTotal,
        }}
      />

      <Toaster position="top-right" richColors />
      
      {/* Video Carousel Modal */}
      <VideoCarouselModal 
        isOpen={showVideoModal} 
        onClose={() => setShowVideoModal(false)} 
      />
      
      {/* Floating WhatsApp Contact Widget */}
      <FloatingContact />
      
      {/* AI Chat Assistant */}
      <AIChatWidget onAutoFill={handleAutoFill} onActivateSections={handleActivateSections} />
    </div>
  );
}

function ProtectedDashboard() {
  const { user } = useAuth();
  
  // If URL contains a valid recovery code (?code=VBX-XXXXXX), bypass login
  // so the recipient of a shared link can view the quote directly. The Dashboard's
  // auto-recover effect will fetch and display it. Only bypass for well-formed
  // codes to avoid exposing the Dashboard to arbitrary URL params.
  const sharedCodeRaw = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('code') : null;
  const hasValidSharedCode = !!(sharedCodeRaw && /^VBX-[A-Z0-9]{6}$/i.test(sharedCodeRaw));
  if (hasValidSharedCode) {
    return <Dashboard />;
  }
  
  // Still checking auth
  if (user === null) {
    return (
      <div className="min-h-screen bg-[#050816] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#aa2aff]" />
      </div>
    );
  }
  
  // Not authenticated
  if (!user) {
    return <LoginPage />;
  }
  
  // Authenticated
  return <Dashboard />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<ProtectedDashboard />} />
            <Route path="/formulario-metaproduccion" element={<MetaproduccionForm />} />
            <Route path="/pago-exitoso" element={<PaymentSuccess />} />
            <Route path="/politica-privacidad" element={<PrivacyPolicy />} />
            <Route path="/terminos-condiciones" element={<TermsConditions />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
